import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Drawer,
  Button,
  Box,
  Typography,
  Paper,
  Avatar,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  AppBar,
  Toolbar,
  Tab,
  TextField,
  InputAdornment,
  Fade,
  ClickAwayListener,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Person as UserIcon,
  Send as SendIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Chat as ChatIcon,
  Add as AddIcon,
  Stop as StopIcon,
  Analytics as AnalyticsIcon,
  Psychology as PsychologyIcon,
  AccessTime as AccessTimeIcon,
  Search as SearchIcon,
  InfoOutlined as InfoOutlinedIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { toSaveDate, toSaveEndDate } from '../../../utilis/date-formats';
import aiChatService, { STREAM_STAGE_LABELS } from '../../../services/ai-chat.service';
import aiClinicalAnalysisValidatorService from '../../../services/ai-clinical-analysis-validator.service';
import AiUsageStats from './AiUsageStats';
import { useAiChatAccess } from '../../../hooks/useAiChatAccess';
import { getMyUsageStats } from '../../../services/ai-chat-management.service';
import { AUTH_ROLES } from '../../../types/auth.type';
import AIIcon from '../../../assets/svgs/AI-Icon.svg';
import { TabContext, TabList, TabPanel } from '@mui/lab';

const PAGE_LABEL = {
  HOSPITAL: 'Hospital Transfers',
  ADMISSION: 'Admissions',
  COMMUNITYTRANSFER: 'Community Transfers',
  DECEASED: 'Deceased',
  OVERALL: 'Overall',
};

const NEW_CHAT_SUGGESTIONS = {
  HOSPITAL: [
    { icon: '📈', label: 'Trends', prompt: 'What trends do you see in hospital transfers over the selected period?' },
    { icon: '🩺', label: 'By diagnosis', prompt: 'Break down hospital transfers by primary diagnosis.' },
    { icon: '👨‍⚕️', label: 'By doctor', prompt: 'Which doctors have the highest hospital transfer rates?' },
  ],
  ADMISSION: [
    { icon: '📈', label: 'Admission trends', prompt: 'What admission trends do you see across the selected period?' },
    { icon: '👨‍⚕️', label: 'By doctor', prompt: 'Break down admissions by attending doctor.' },
    { icon: '💳', label: 'By insurance', prompt: 'How do admissions break down by insurance/payer?' },
  ],
  COMMUNITYTRANSFER: [
    { icon: '📈', label: 'Transfer trends', prompt: 'Summarize community transfer trends over this period.' },
    { icon: '👨‍⚕️', label: 'By doctor', prompt: 'Which doctors have the highest community transfer rates?' },
    { icon: '📅', label: 'By day of week', prompt: 'Are community transfers concentrated on specific days?' },
  ],
  DECEASED: [
    { icon: '📈', label: 'Trends', prompt: 'What trends do you see in deceased outcomes for this period?' },
    { icon: '👨‍⚕️', label: 'By doctor', prompt: 'Break down deceased by attending doctor.' },
    { icon: '📅', label: 'By day of week', prompt: 'Are deceased concentrated on specific days?' },
  ],
  OVERALL: [
    { icon: '📊', label: 'Overall summary', prompt: 'Give me an overall summary of dashboard performance for the selected period.' },
    { icon: '⚖️', label: 'Compare periods', prompt: 'Compare this period to the previous one — what changed most?' },
    { icon: '🚨', label: 'Anything alarming?', prompt: 'Flag anything alarming or unusual in the current data.' },
    { icon: '💡', label: 'Where to focus', prompt: 'What should I focus on to improve these metrics?' },
  ],
};

const NOTES_SUGGESTIONS = {
  HOSPITAL: [
    { icon: '🚨', label: 'Early warning signs', prompt: 'What early warning signs appeared most often before hospitalization?' },
    { icon: '⚠️', label: 'Avoidable signs', prompt: 'Which early warning signs were most associated with likely avoidable hospitalizations?' },
    { icon: '🔁', label: 'Recurrence risk', prompt: 'Which residents or resident-types show the highest clinical recurrence risk?' },
    { icon: '⏱️', label: '24–72h signals', prompt: 'What signs tend to appear 24 to 72 hours before hospitalization?' },
    { icon: '🧩', label: 'Similar patterns', prompt: 'Which hospitalizations appear clinically similar enough to represent the same preventable pattern?' },
    { icon: '🎯', label: 'Top 3 patterns', prompt: 'What are the top 3 clinical patterns the facility should focus on next?' },
  ],
  ADMISSION: [
    { icon: '⏱️', label: 'First 24h priorities', prompt: 'What are the top priorities during the first 24 hours after admission?' },
    { icon: '🛡️', label: 'Prevent hospitalization', prompt: 'What can we do in-house to prevent hospitalization?' },
    { icon: '🛠️', label: 'Required capabilities', prompt: 'What resources or capabilities are required to safely manage this resident?' },
    { icon: '🏠', label: 'Discharge barriers', prompt: 'What are the biggest barriers to successful recovery and discharge home?' },
    { icon: '🎯', label: 'Missed prevention', prompt: 'What hospitalization prevention opportunities were missed?' },
    { icon: '📊', label: 'Clinical risk level', prompt: 'What is the overall clinical risk level of this admission?' },
  ],
};

const CLINICAL_TAB_PAGES = new Set(['HOSPITAL', 'ADMISSION']);

const getNewChatSuggestions = (pageType, activeTab) => {
  const upper = pageType?.toUpperCase();
  if (activeTab === 1) return NOTES_SUGGESTIONS[upper] || NOTES_SUGGESTIONS.HOSPITAL;
  return NEW_CHAT_SUGGESTIONS[upper] || NEW_CHAT_SUGGESTIONS.OVERALL;
};

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'isMinimized' && prop !== 'isExpanded',
})(({ theme, isMinimized, isExpanded }) => ({
  zIndex: 1300, // Ensure it's above header
  '& .MuiDrawer-paper': {
    width: isMinimized ? 60 : (isExpanded ? '60%' : 700),
    top: 'auto', // Account for main header height
    bottom: 0,
    height: isMinimized ? 60 : 'calc(100vh - 220px)', // Full height minus header
    [theme.breakpoints.down('sm')]: {
      width: isMinimized ? 60 : '100%',
      top: 56, // Smaller header on mobile
      height: isMinimized ? 60 : 'calc(100vh - 56px)',
    },
    borderTopLeftRadius: 16,
    borderBottomLeftRadius: 16,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    transition: 'all 0.3s ease-in-out',
    overflow: isMinimized ? 'visible' : 'hidden',
  },
}));

const MinimizedButton = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: 20,
  right: 20,
  zIndex: 1400,
  width: 60,
  height: 60,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'scale(1.1)',
    boxShadow: '0 6px 25px rgba(0, 0, 0, 0.2)',
  },
}));

const ChatContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: 0,
  backgroundColor: theme.palette.grey[50],
  minHeight: 0, // Important for flex child to shrink
  maxHeight: '100%', // Ensure it doesn't exceed parent height
  // Disable Chrome's scroll-anchoring heuristic. When the assistant streams
  // in new content, scroll-anchoring can shift scrollTop to keep some
  // previous anchor visible, which fights our explicit scroll-to-bottom and
  // causes visible flicker. We manage scroll position ourselves.
  overflowAnchor: 'none',
  '&::-webkit-scrollbar': {
    width: 8,
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: theme.palette.grey[100],
    borderRadius: 4,
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.grey[400],
    borderRadius: 4,
    '&:hover': {
      backgroundColor: theme.palette.grey[600],
    },
  },
  // Firefox scrollbar styling
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.grey[400]} ${theme.palette.grey[100]}`,
}));

// Set of known stage-label strings — used so handleStopRequest can tell
// "user has seen partial answer tokens" from "user has only seen a stage label".
const STAGE_LABELS = new Set(Object.values(STREAM_STAGE_LABELS));

// Memoized wrapper around ReactMarkdown. During streaming, setMessages() fires
// ~16x/sec — which re-renders the whole message list, including every already-
// settled assistant message. Without memoization, each settled message would
// re-parse its (unchanged) markdown on every tick, causing a ton of wasted
// work and visible flicker. React.memo short-circuits the re-parse when the
// content string hasn't changed.
const MemoMarkdown = React.memo(
  function MemoMarkdown({ content }) {
    return (
      <ReactMarkdown remarkPlugins={[remarkGfm]}>
        {content}
      </ReactMarkdown>
    );
  },
  (prev, next) => prev.content === next.content
);

// ChatGPT-style shimmer for the in-flight "thinking" stage label. A lighter
// highlight sweeps left-to-right across the text while we wait for tokens to
// start streaming, so the user knows the agent is actively working.
const ShimmerText = styled('span')(() => ({
  display: 'inline-block',
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: 1.4,
  // High-contrast shimmer — light grey base with a dark sweep passing
  // through. Fixed colours (not theme palette) so the effect is clearly
  // visible against any chat background.
  backgroundImage: `linear-gradient(90deg,
    #c8c8c8 0%,
    #c8c8c8 35%,
    #1a1a1a 50%,
    #c8c8c8 50%,
    #c8c8c8 100%)`,
  backgroundSize: '300% 100%',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  color: 'transparent',
  animation: 'aiShimmerSweep 3s linear infinite',
  '@keyframes aiShimmerSweep': {
    '0%':   { backgroundPosition: '200% 0' },
    '100%': { backgroundPosition: '-100% 0' },
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    backgroundPosition: '50% 0',
  },
}));

const MessageBubble = styled(Paper)(({ theme, isUser }) => ({
  padding: isUser ? theme.spacing(1, 1.5) : 0,
  marginBottom: theme.spacing(0.5),
  maxWidth: isUser ? '85%' : '100%',
  alignSelf: isUser ? 'flex-end' : 'flex-start',
  // AI replies render transparently (no bubble) so the answer sits flush with
  // the chat background; user messages still get the chat-bubble treatment.
  backgroundColor: isUser ? theme.palette.primary.main : 'transparent',
  color: isUser ? theme.palette.primary.contrastText : theme.palette.text.primary,
  borderRadius: isUser ? '16px 16px 4px 16px' : 0,
  boxShadow: isUser ? theme.shadows[1] : 'none',
  position: 'relative',
  wordBreak: 'break-word',
  marginLeft: isUser ? 'auto' : 0,
  fontFamily: 'manrope',
  fontSize: '14px',
  lineHeight: '1.4',
  fontWeight: 400,
}));

const MessageContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  minHeight: '100%', // Ensure it takes full height
  paddingBottom: 8, // Add some bottom padding for better UX
});

const MessageRow = styled(Box)(({ isUser }) => ({
  display: 'flex',
  flexDirection: isUser ? 'row-reverse' : 'row',
  alignItems: 'flex-start',
  gap: 6,
  marginBottom: 12,
}));

// DetailsButton - Commented out but kept for potential future use
// eslint-disable-next-line no-unused-vars
const DetailsButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontSize: '0.7rem',
  padding: theme.spacing(0.25, 1),
  borderRadius: 4,
  borderColor: theme.palette.grey[400],
  color: theme.palette.text.primary,
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.grey[50],
  },
}));

const TruncatedFilledChip = ({ label, maxWidth = 200, sx }) => {
  const labelRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = labelRef.current;
    if (!el) return;
    const check = () => setIsTruncated(el.scrollWidth > el.clientWidth + 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    window.addEventListener('resize', check);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', check);
    };
  }, [label, maxWidth]);

  return (
    <Tooltip
      title={label}
      arrow
      placement="top"
      disableHoverListener={!isTruncated}
      disableFocusListener={!isTruncated}
      disableTouchListener={!isTruncated}
    >
      <Chip
        label={
          <Box
            ref={labelRef}
            component="span"
            sx={{
              display: 'inline-block',
              maxWidth: `${maxWidth}px`,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              verticalAlign: 'middle',
            }}
          >
            {label}
          </Box>
        }
        sx={{
          backgroundColor: 'rgba(72, 121, 245, 0.1)',
          color: 'primary.main',
          height: 24,
          fontSize: '11px',
          '& .MuiChip-label': { px: 1, overflow: 'visible' },
          ...sx,
        }}
      />
    </Tooltip>
  );
};

const ChipGroup = ({ labels = [], chipMaxWidth = 100, gap = 8, widthRatio = 0.5 }) => {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(labels.length);
  const [overflowTooltipOpen, setOverflowTooltipOpen] = useState(false);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const compute = () => {
      const parent = container.parentElement;
      if (!parent) return;
      const allowed = parent.clientWidth * widthRatio;
      const items = Array.from(measure.children);
      const widths = items.map((el) => el.getBoundingClientRect().width);
      const overflowChip = items[items.length - 1];
      const overflowWidth = overflowChip ? overflowChip.getBoundingClientRect().width : 0;

      let used = 0;
      let count = 0;
      for (let i = 0; i < labels.length; i++) {
        const next = used + widths[i] + (count > 0 ? gap : 0);
        const remaining = labels.length - (i + 1);
        const reserve = remaining > 0 ? overflowWidth + gap : 0;
        if (next + reserve <= allowed) {
          used = next;
          count = i + 1;
        } else {
          break;
        }
      }
      setVisibleCount(Math.max(count, labels.length === 0 ? 0 : 1));
    };

    compute();
    const ro = new ResizeObserver(compute);
    if (container.parentElement) ro.observe(container.parentElement);
    window.addEventListener('resize', compute);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', compute);
    };
  }, [labels, gap, widthRatio]);

  const hidden = labels.slice(visibleCount);

  return (
    <>
      {/* Hidden measurement layer */}
      <Box
        ref={measureRef}
        sx={{
          position: 'absolute',
          visibility: 'hidden',
          height: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
          display: 'flex',
          gap: `${gap}px`,
        }}
        aria-hidden
      >
        {labels.map((l, i) => (
          <TruncatedFilledChip key={`m-${i}`} label={l} maxWidth={chipMaxWidth} />
        ))}
        <Chip
          label={`+${labels.length}`}
          sx={{
            backgroundColor: 'rgba(72, 121, 245, 0.1)',
            color: 'primary.main',
            height: 24,
            fontSize: '11px',
            '& .MuiChip-label': { px: 1 },
          }}
        />
      </Box>

      {/* Visible chips */}
      <Box ref={containerRef} sx={{ display: 'flex', alignItems: 'center', gap: `${gap}px`, minWidth: 0 }}>
        {labels.slice(0, visibleCount).map((l, i) => (
          <TruncatedFilledChip key={i} label={l} maxWidth={chipMaxWidth} />
        ))}
        {hidden.length > 0 && (
          <ClickAwayListener onClickAway={() => setOverflowTooltipOpen(false)}>
            <Tooltip
              arrow
              placement="top"
              open={overflowTooltipOpen}
              onClose={() => setOverflowTooltipOpen(false)}
              onOpen={() => setOverflowTooltipOpen(true)}
              disableFocusListener
              title={
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {hidden.map((l, i) => (
                    <Box key={i} sx={{ fontSize: '11px' }}>{l}</Box>
                  ))}
                </Box>
              }
            >
              <Chip
                label={`+${hidden.length}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setOverflowTooltipOpen((prev) => !prev);
                }}
                sx={{
                  backgroundColor: 'rgba(72, 121, 245, 0.1)',
                  color: 'primary.main',
                  height: 24,
                  fontSize: '11px',
                  '& .MuiChip-label': { px: 1 },
                  cursor: 'pointer',
                }}
              />
            </Tooltip>
          </ClickAwayListener>
        )}
      </Box>
    </>
  );
};


const AiChatDrawer = ({
  open,
  onClose,
  pageType = 'HOSPITAL',
  pageData = {},
  initialMessage = null,
  onMinimizeStateChange = null,
  handleOnClickReport = null,
  downloadExcel = null,
  conversationId: propConversationId = null,
  contextPreloaded = false,
  filter = null,
  vectorStoreId = null,
  onVectorStoreIdUpdate = null,
  disabled = false,
  loadingMessage = null
}) => {
  const [messages, setMessages] = useState([]);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [dateInfoTooltipOpen, setDateInfoTooltipOpen] = useState(false);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [conversationId, setConversationId] = useState(propConversationId);
  const [isMinimized, setIsMinimized] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeTab, setActiveTab] = useState(0); // 0 = Trend Analysis, 1 = Clinical Analysis
  // Force Trend Analysis on pages that don't support Clinical Analysis.
  // Currently Hospital and Admission both support it (each with its own
  // dedicated backend pipeline); other pages snap back to Trend.
  useEffect(() => {
    const allowed = CLINICAL_TAB_PAGES.has(pageType?.toUpperCase());
    if (!allowed && activeTab !== 0) {
      setActiveTab(0);
    }
  }, [pageType, activeTab]);
  const [modeAnchorEl, setModeAnchorEl] = useState(null);
  const [reasoningMode, setReasoningMode] = useState('precision'); // Always use precision mode
  const [isExpanded] = useState(false); // Track if drawer is expanded to 50% width
  const [usageStats, setUsageStats] = useState(null); // Track usage stats for display
  const [isClosing, setIsClosing] = useState(false); // Track if drawer is closing (API call in progress)
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const isMinimizedRef = useRef(isMinimized); // 🔔 Track current minimized state for notification logic
  // Smart auto-scroll: only follow the stream when the user is near the bottom.
  // `chatContainerElRef` is the live DOM node (or null if unmounted, e.g. when
  // the History tab is active). We use a callback ref so listeners always
  // attach to the current node even though MUI's TabPanel mounts/unmounts it.
  const chatContainerElRef = useRef(null);
  const stickToBottomRef = useRef(true); // true => auto-follow is on
  const detachScrollListenersRef = useRef(null);
  // AbortController for the in-flight streaming request. Held in a ref so
  // handleStopRequest (and the drawer-close / component-unmount paths) can
  // cancel the underlying fetch and the word-ticker.
  const streamAbortRef = useRef(null);
  const sendInProgressRef = useRef(false);
  // Live snapshot of what the streaming message currently displays (tokens
  // the ticker has flushed). handleStopRequest reads this to tell the
  // backend which content to persist for the stopped reply.
  const displayedContentRef = useRef({ conversationId: null, content: '' });

  const streamingTurnRef = useRef(null);
  const conversationIdRef = useRef(propConversationId);
  // Get AI chat access and usage stats
  // eslint-disable-next-line no-unused-vars
  const { usage: initialUsage, loading: usageLoading } = useAiChatAccess();
  // Page data ref to avoid re-creating callbacks/effects when large context object changes
  const pageDataRef = useRef(pageData);
  const [activeAssistantTab, setActiveAssistantTab] = useState('chat');
  useEffect(() => {
    pageDataRef.current = pageData;
  }, [pageData]);

  const handleAssistantTabChange = (event, newValue) => {
    setActiveAssistantTab(newValue);
  };

  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  // History tab state
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [historySearch, setHistorySearch] = useState('');
  const [historyLoadingId, setHistoryLoadingId] = useState(null);
  const [debouncedHistorySearch, setDebouncedHistorySearch] = useState('');

  // Card action menu / rename / delete state
  const [actionMenu, setActionMenu] = useState({ anchorEl: null, item: null });
  const [renameDialog, setRenameDialog] = useState({ open: false, id: null, title: '' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [clearAllDialog, setClearAllDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  // When a past conversation is loaded, pin its stored date range in the header
  const [loadedConversationDateRange, setLoadedConversationDateRange] = useState(null);

  // Debounce search input (350ms)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedHistorySearch(historySearch.trim()), 350);
    return () => clearTimeout(t);
  }, [historySearch]);

  // Reasoning mode configurations - wrapped in useMemo to prevent recreation on every render
  const reasoningModes = React.useMemo(() => ({
    fast: {
      label: 'Fast',
      description: 'Quick responses using faster model with minimal context for speed',
      icon: <PsychologyIcon fontSize="small" />,
      reasoning: 'low', // Fast mode - minimal reasoning
      maxTokens: 3000,
      model: 'gpt-4o-mini', // Use faster model for quick responses
      color: '#4caf50',
      promptStyle: 'minimal',
      skipVectorStore: true,
      skipClassificationAI: true,
      useEnhancedContext: true,
      useDirectResponse: true
    },
    auto: {
      label: 'Auto',
      description: 'Automatically adapts reasoning depth based on query complexity',
      icon: <PsychologyIcon fontSize="small" />,
      reasoning: 'auto', // Auto mode - model decides when to use reasoning
      maxTokens: 4000,
      model: 'gpt-4o',
      color: '#2196f3',
      promptStyle: 'concise',
      skipVectorStore: true,
      skipClassificationAI: true,
      useEnhancedContext: true,
      useDirectResponse: true
    },
    detailed: {
      label: 'Detailed',
      description: 'Thorough analysis with step-by-step reasoning and context',
      icon: <PsychologyIcon fontSize="small" />,
      reasoning: 'medium',
      maxTokens: 6000,
      model: 'gpt-4o', // Fast model for detailed analysis
      color: '#ff9800',
      promptStyle: 'detailed',
      skipVectorStore: true,
      skipClassificationAI: true,
      useEnhancedContext: true,
      useDirectResponse: true
    },
    expert: {
      label: 'Expert',
      description: 'Deep multi-step reasoning with comprehensive insights and recommendations',
      icon: <PsychologyIcon fontSize="small" />,
      reasoning: 'high',
      maxTokens: 10000,
      model: 'gpt-4o', // GPT-4o with high reasoning for expert analysis
      color: '#9c27b0',
      promptStyle: 'expert',
      skipVectorStore: true,
      skipClassificationAI: true,
      useEnhancedContext: true,
      useDirectResponse: true,
      useToolBased: false, // Expert uses direct response, not tools
      enableTools: false
    },
    precision: {
      label: 'Precision',
      description: 'Database-driven queries for 100% accurate cross-filtering and counts',
      icon: <PsychologyIcon fontSize="small" />,
      reasoning: 'high',
      maxTokens: 10000,
      model: 'gpt-4o', // GPT-4o for tool-based precision queries
      color: '#00bcd4',
      promptStyle: 'concise',
      skipVectorStore: true,
      skipClassificationAI: true,
      useEnhancedContext: false, // Don't need enhanced context - tools query DB directly
      useDirectResponse: false, // Use tool-based response instead
      useToolBased: true, // Enable tool-based DB queries
      enableTools: true
    }
  }), []);

  // Redux state
  const { auth, activeFacilities, comparisonRanges, rangesSet, facilities } = useSelector(state => ({
    auth: state.auth,
    activeFacilities: state.activeFacilities.facilities,
    comparisonRanges: state.comparison?.comparisonRanges,
    rangesSet: state.comparison?.rangesSet,
    facilities: state.facility
  }));

  // Map facilityId → name for rendering history chips
  const facilityNameById = React.useMemo(() => {
    const map = new Map();
    (facilities || []).forEach(f => {
      const id = f?._id || f?.id;
      if (id) map.set(String(id), f?.name || f?.facilityName || '');
    });
    return map;
  }, [facilities]);

  // Fetch conversation history when the History tab is active or when search changes
  useEffect(() => {
    if (activeAssistantTab !== 'history' || !open) return;

    let cancelled = false;
    const fetchHistory = async () => {
      setHistoryLoading(true);
      setHistoryError(null);
      try {
        const items = await aiChatService.getAgentConversations({
          pageType: pageType || undefined,
          search: debouncedHistorySearch || undefined,
          limit: 30
        });
        if (!cancelled) setHistoryItems(Array.isArray(items) ? items : []);
      } catch (err) {
        if (!cancelled) {
          setHistoryError(err?.message || 'Failed to load conversations');
          setHistoryItems([]);
        }
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    };

    fetchHistory();
    return () => { cancelled = true; };
  }, [activeAssistantTab, open, debouncedHistorySearch, pageType]);

  // Load a past conversation into the Chat tab
  const loadConversation = async (id) => {
    if (!id || historyLoadingId) return;

    if (id === conversationId) {
      setActiveAssistantTab('chat');
      return;
    }

    setHistoryLoadingId(id);
    setHistoryError(null);
    try {
      const data = await aiChatService.getAgentConversationHistory(id);
      const rawMsgs = (data?.messages || []).filter(m => m.role === 'user' || m.role === 'assistant');
      const transformed = rawMsgs.map(m => ({
        id: m.id || `${m.role}-${m.timestamp}`,
        type: m.role === 'user' ? 'user' : 'ai',
        content: m.content,
        timestamp: m.timestamp ? new Date(m.timestamp) : new Date(),
        isWelcome: false,
        metadata: m.metadata || undefined
      }));

      // Default the next-message mode to the mode of the last assistant turn
      // so continuing the conversation feels seamless. Older conversations
      // without recorded mode fall back to the user's current activeTab.
      const lastAssistant = [...rawMsgs].reverse().find(m => m.role === 'assistant');
      const conversationMode = lastAssistant?.metadata?.mode || data?.context?.mode || null;
      const isClinical = conversationMode
        ? conversationMode === 'clinical'
        : activeTab === 1;

      const targetConvId = data?.id || id;
      let messagesToShow = transformed;
      const liveTurn = streamingTurnRef.current;
      if (liveTurn && liveTurn.conversationId === targetConvId) {
        const lastInList = transformed[transformed.length - 1];
        const stashedUser = liveTurn.userMessage;
        const backendHasAi = lastInList?.type === 'ai';
        if (!backendHasAi) {
          const userAlreadyInHistory = lastInList?.type === 'user'
            && lastInList?.content === stashedUser?.content;
          const stashedAi = {
            id: liveTurn.aiMessageId,
            type: 'ai',
            content: liveTurn.isThinking
              ? (liveTurn.stageLabel || '')
              : (liveTurn.content || ''),
            timestamp: liveTurn.aiTimestamp || new Date(),
            suggestions: [],
            insights: [],
            actions: [],
            hasActions: false,
            executedActions: [],
            hasExecutedActions: false,
            isStreaming: liveTurn.isStreaming,
            isThinking: liveTurn.isThinking,
            showReasoningIndicator: liveTurn.showReasoningIndicator,
            reasoningMode: 'precision',
            metadata: liveTurn.metadata
          };
          messagesToShow = userAlreadyInHistory
            ? [...transformed, stashedAi]
            : [...transformed, stashedUser, stashedAi];
        }
      }

      setMessages(messagesToShow);
      setActiveTab(isClinical ? 1 : 0);

      setConversationId(targetConvId);
      setLoadedConversationDateRange(
        data?.dateRange?.startDate && data?.dateRange?.endDate ? data.dateRange : null
      );
      setActiveAssistantTab('chat');
    } catch (err) {
      setHistoryError(err?.message || 'Failed to load conversation');
    } finally {
      setHistoryLoadingId(null);
    }
  };

  const handleActionMenuOpen = (e, item) => {
    e.stopPropagation();
    setActionMenu({ anchorEl: e.currentTarget, item });
  };

  const handleActionMenuClose = () => {
    setActionMenu({ anchorEl: null, item: null });
  };

  const handleRenameClick = () => {
    const item = actionMenu.item;
    if (!item) return;
    setRenameDialog({ open: true, id: item.id, title: item.title || '' });
    handleActionMenuClose();
  };

  const handleDeleteClick = () => {
    const item = actionMenu.item;
    if (!item) return;
    setDeleteDialog({ open: true, id: item.id });
    handleActionMenuClose();
  };

  const handleRenameSubmit = async () => {
    const { id, title } = renameDialog;
    const trimmed = (title || '').trim();
    if (!id || !trimmed || actionLoading) return;
    setActionLoading(true);
    setHistoryError(null);
    try {
      const updated = await aiChatService.renameAgentConversation(id, trimmed);
      const nextTitle = updated?.title || trimmed;
      setHistoryItems(prev => prev.map(it => it.id === id ? { ...it, title: nextTitle } : it));
      setRenameDialog({ open: false, id: null, title: '' });
    } catch (err) {
      setHistoryError(err?.message || 'Failed to rename conversation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleClearAllConfirm = async () => {
    if (actionLoading) return;
    setActionLoading(true);
    setHistoryError(null);
    try {
      await aiChatService.deleteAllAgentConversations({
        pageType: pageType || undefined
      });
      const clearedIds = new Set(historyItems.map(i => i.id));
      setHistoryItems([]);
      if (conversationId && clearedIds.has(conversationId)) {
        handleClearChat();
      }
      setClearAllDialog(false);
    } catch (err) {
      setHistoryError(err?.message || 'Failed to clear conversations');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    const { id } = deleteDialog;
    if (!id || actionLoading) return;
    setActionLoading(true);
    setHistoryError(null);
    try {
      await aiChatService.deleteAgentConversation(id);
      setHistoryItems(prev => prev.filter(it => it.id !== id));
      if (conversationId === id) {
        handleClearChat();
      }
      setDeleteDialog({ open: false, id: null });
    } catch (err) {
      setHistoryError(err?.message || 'Failed to delete conversation');
    } finally {
      setActionLoading(false);
    }
  };

  // Check if prompt limit is exceeded (only for non-SUPER users)
  // Only check user limit, not facility limit
  const isLimitExceeded = React.useMemo(() => {
    if (auth?.role?.slug === AUTH_ROLES.SUPER) {
      return false; // SUPER users have unlimited access
    }
    if (!usageStats) {
      return false; // Don't disable if we don't have usage stats yet
    }
    // Only check user limit
    if (usageStats.remaining <= 0) {
      return true;
    }
    return false;
  }, [usageStats, auth?.role?.slug]);

  // Refresh usage stats after successful message
  // Only fetch user limit, not facility limit
  const refreshUsageStats = useCallback(async () => {
    setUsageStats((prev) => {
      if (!prev || typeof prev.used !== 'number' || typeof prev.limit !== 'number') {
        return prev;
      }
      const nextUsed = Math.min(prev.limit, prev.used + 1);
      const nextRemaining = typeof prev.remaining === 'number'
        ? Math.max(0, prev.remaining - 1)
        : Math.max(0, prev.limit - nextUsed);
      return { ...prev, used: nextUsed, remaining: nextRemaining };
    });

    const fetchAndApply = async () => {
      try {
        const response = await getMyUsageStats(null);
        if (response?.success && response?.data) {
          setUsageStats((prev) => {
            if (prev && typeof prev.used === 'number' && typeof response.data.used === 'number'
                && response.data.used < prev.used) {
              return prev;
            }
            return response.data;
          });
        }
      } catch (error) {
        console.error('Error refreshing usage stats:', error);
      }
    };

    setTimeout(fetchAndApply, 1500);
  }, []);

  // Initialize usage stats from hook (only user limit, no facility limit)
  useEffect(() => {
    if (initialUsage) {
      setUsageStats(initialUsage);
    }
  }, [initialUsage]);

  // CRITICAL: Force precision mode for all tabs - ensures tool-based processing is always used
  useEffect(() => {
    if (reasoningMode !== 'precision') {
      setReasoningMode('precision');
    }
  }, [reasoningMode]);

  // Update conversationId when prop changes
  useEffect(() => {
    if (propConversationId && propConversationId !== conversationId) {
      setConversationId(propConversationId);
    }
  }, [propConversationId, conversationId]);

  const scrollToBottom = (opts = {}) => {
    const { force = false } = opts;
    if (force) stickToBottomRef.current = true;
    const el = chatContainerElRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    } else if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto', block: 'end' });
    }
  };

  // Callback ref for <ChatContainer>. Fires with the DOM node on mount and
  // with `null` on unmount. TabPanel mounts/unmounts the node each time the
  // user toggles between Chat and History tabs, so this ensures listeners
  // always track the current live element.
  //
  // Hysteresis thresholds: once the user is past `DISABLE_PX` from the
  // bottom, auto-follow turns off. It only turns back on when they scroll
  // to within `ENABLE_PX`. The wide middle band prevents trackpad inertia
  // or momentum flicks from repeatedly toggling the state.
  // (Scroll listeners are installed by a separate useEffect that queries the
  //  DOM for `[data-ai-chat-scroll-container]`. MUI's styled() + TabPanel +
  //  Fade nesting was swallowing callback refs, so DOM-query is the reliable
  //  path.)

  // Play notification sound when new message arrives while minimized
  const playNotificationSound = useCallback(() => {
    try {
      // Create a simple notification beep using Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Configure pleasant notification sound (two-tone beep)
      oscillator.frequency.value = 800; // Hz
      oscillator.type = 'sine';

      // Fade in and out for smoother sound
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.2);

      // Second tone for pleasant notification
      setTimeout(() => {
        const oscillator2 = audioContext.createOscillator();
        const gainNode2 = audioContext.createGain();

        oscillator2.connect(gainNode2);
        gainNode2.connect(audioContext.destination);

        oscillator2.frequency.value = 1000; // Slightly higher pitch
        oscillator2.type = 'sine';

        gainNode2.gain.setValueAtTime(0, audioContext.currentTime);
        gainNode2.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.01);
        gainNode2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);

        oscillator2.start(audioContext.currentTime);
        oscillator2.stop(audioContext.currentTime + 0.15);
      }, 100);
    } catch (error) {
      console.error('Failed to play notification sound:', error);
    }
  }, []);

  // Create clinical analysis-focused context for clinical analysis tab
  // This uses the SAME full context as trend analysis but adds a flag to focus on clinical data
  const createNotesContext = useCallback(() => {
    // Use the same full context as trend analysis - no need to duplicate data
    const notesContext = {
      ...pageDataRef.current, // Use latest pageData without forcing re-renders
      isNotesAnalysis: true, // Flag to tell backend to focus on clinical analysis
      notesAnalysisInstructions: {
        focus: "Analyze patient data with all fields",
        behavior: [
          "Use all filters, cards, and data context like trend analysis",
          "When analyzing data, use ALL patient fields (demographics, clinical, notes, custom fields)",
          "Apply filters (ER, DC, facilities, dates) to find relevant patients",
          "Analyze complete patient data including notes, diagnoses, and custom fields",
          "Provide comprehensive clinical insights based on all available data"
        ],
        examples: [
          "User: 'Analyze patient data for ER patients' → Filter for ER patients, analyze all their fields",
          "User: 'What clinical patterns do you see?' → Analyze all patient data for clinical insights",
          "User: 'Show patient data from Facility A' → Filter for Facility A, analyze complete patient records"
        ]
      }
    };

    return notesContext;
  }, []);

  const handleSendMessage = useCallback(async (messageText = null) => {
    const message = messageText || inputMessage.trim();

    // CRITICAL: Prevent double-clicks and simultaneous requests.
    // Use ref as immediate guard — isLoading state is stale in the closure
    // until React re-renders, so fast double-clicks can bypass the state check.
    if (!message || isLoading || sendInProgressRef.current) {
      return;
    }
    sendInProgressRef.current = true;

    // 🚨 CRITICAL: Validate message BEFORE calling API (Clinical Analysis tab validation)
    const isClinicalAnalysisTab = activeTab === 1;
    const validation = aiClinicalAnalysisValidatorService.validate(message, isClinicalAnalysisTab);

    if (validation.shouldBlock) {
      // Add user message to chat
      const userMessage = {
        id: Date.now(),
        type: 'user',
        content: message,
        timestamp: new Date(),
        metadata: { mode: 'clinical' }
      };
      setMessages(prev => [...prev, userMessage]);

      // Add validation message with "Switch to Trend Analysis" button
      const validationMessageObj = aiClinicalAnalysisValidatorService.createValidationMessageObject(
        validation.message,
        validation.actionType
      );
      setMessages(prev => [...prev, { ...validationMessageObj, metadata: { mode: 'clinical' } }]);

      // Clear input
      setInputMessage('');

      // Do NOT call API - validation handled locally
      return;
    }

    // 🔔 Track if request started while minimized for notification logic

    const messageMode = activeTab === 1 ? 'clinical' : 'trend';

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: message,
      timestamp: new Date(),
      metadata: { mode: messageMode }
    };

    setMessages(prev => [...prev, userMessage]);

    // The user just sent something — always jump to the bottom and re-enable
    // auto-follow for the reply, even if they had scrolled up earlier.
    scrollToBottom({ force: true });

    setInputMessage('');
    setIsLoading(true);
    setError(null);
    // New question uses the LIVE dashboard date range/facility — drop any pinned range from a loaded chat
    setLoadedConversationDateRange(null);

    // DON'T auto-expand when sending message - let user keep it minimized while waiting
    // This allows notification to work properly when response arrives
    // User can manually expand by clicking the floating button

    // Check if vector store needs to be created (first time setup)
    const isVectorStoreCreation = !vectorStoreId || vectorStoreId === null;

    // Create initial AI message with thinking indicator
    // Show different message if vector store is being created (first time setup takes longer)
    const aiMessageId = Date.now() + 1;
    const initialAiMessage = {
      id: aiMessageId,
      type: 'ai',
      // Stage labels land here via onStage (e.g. "Preparing data for trend
      // analysis…", "Composing your answer…"). Leaving it empty keeps the UI
      // minimal until the first stage event arrives.
      content: '',
      timestamp: new Date(),
      suggestions: [],
      insights: [],
      actions: [],
      hasActions: false,
      executedActions: [],
      hasExecutedActions: false,
      isStreaming: true,
      isThinking: true,
      showReasoningIndicator: true, // Show GPT-5 Pro reasoning indicator
      reasoningMode: 'precision', // Always use precision mode with tool-based processing
      isVectorStoreCreation, // Track if this is initial setup
      metadata: { mode: messageMode }
    };

    setMessages(prev => [...prev, initialAiMessage]);

    streamingTurnRef.current = {
      conversationId: conversationId || null,
      aiMessageId,
      userMessage,
      aiTimestamp: initialAiMessage.timestamp,
      content: '',
      stageLabel: '',
      isStreaming: true,
      isThinking: true,
      showReasoningIndicator: true,
      metadata: { mode: messageMode },
      completed: false
    };

    let cleanupVisibilityListener = null;

    try {

      // Create context based on active tab
      let context;
      let conversationHistory; // eslint-disable-line no-unused-vars

      // Extract only serializable properties from precision mode (exclude icon, color)
      // CRITICAL: Always use precision mode with tool-based processing for both tabs
      const serializableReasoningMode = {
        label: reasoningModes.precision.label,
        description: reasoningModes.precision.description,
        reasoning: reasoningModes.precision.reasoning,
        maxTokens: reasoningModes.precision.maxTokens,
        model: reasoningModes.precision.model,
        promptStyle: reasoningModes.precision.promptStyle,
        // CRITICAL: Include optimization flags for backend
        skipVectorStore: reasoningModes.precision.skipVectorStore,
        skipClassificationAI: reasoningModes.precision.skipClassificationAI,
        useEnhancedContext: reasoningModes.precision.useEnhancedContext,
        useDirectResponse: reasoningModes.precision.useDirectResponse,
        // Tool-based processing flags for accurate DB queries
        useToolBased: reasoningModes.precision.useToolBased, // true
        enableTools: reasoningModes.precision.enableTools    // true
      };

      // Extract date range from dashboard filter (always pass if available)
      // Use toSaveDate/toSaveEndDate to match dashboard API date formatting:
      // - startDate: start of day
      // - endDate: end of day (23:59:59.999)
      const startDate = filter?.startDate ? toSaveDate(filter.startDate) : null;
      const endDate = filter?.endDate ? toSaveEndDate(filter.endDate) : null;

      if (activeTab === 0) {
        // Trend Analysis - use existing context
        context = aiChatService.createContext({
          pageType,
          pageData: pageDataRef.current,
          facilityIds: activeFacilities,
          accountId: auth?.accountId,
          conversationId,
          vectorStoreId,
          reasoningMode: serializableReasoningMode, // Pass only serializable reasoning mode config
          startDate,  // From dashboard filter
          endDate     // From dashboard filter
        });
      } else {
        // Clinical Analysis - use same context but with notes focus flag
        const notesPageData = createNotesContext();

        context = aiChatService.createContext({
          pageType,
          pageData: notesPageData, // Use notes-focused pageData
          facilityIds: activeFacilities,
          accountId: auth?.accountId,
          conversationId,
          vectorStoreId,
          reasoningMode: serializableReasoningMode, // Precision mode with tool-based processing
          startDate,  // From dashboard filter
          endDate     // From dashboard filter
        });
      }

      conversationHistory = aiChatService.formatConversationHistory(messages, 10); // eslint-disable-line no-unused-vars

      // Extract comparison dates for flag/trend analysis
      const comparisonStartDate = rangesSet && comparisonRanges?.[0]?.startDate
          ? toSaveDate(comparisonRanges[0].startDate) : null;
      const comparisonEndDate = rangesSet && comparisonRanges?.[0]?.endDate
          ? toSaveEndDate(comparisonRanges[0].endDate) : null;

      // Call AI Agent streaming endpoint — emits stage events as the multi-agent
      // pipeline progresses, streams the final answer's tokens as the summarization
      // LLM generates them, then fires a `complete` event with the mapped response.
      // Falls back to the synchronous endpoint if the stream fails mid-flight.
      //
      // Token pacing: OpenAI bursts tokens (sometimes tens at once, then pauses).
      // We buffer incoming tokens in `pendingChars` and release them to the UI at
      // a steady rate via `ticker`. This smooths bursts, hides mid-stream gaps,
      // and reduces markdown-reparse jumping because characters appear evenly.
      let apiResult;
      let streamedContent = '';
      let pendingChars = '';
      // Reset the per-request displayed-content snapshot. handleStopRequest
      // reads from this ref when Stop is pressed mid-stream.
      displayedContentRef.current = { conversationId: conversationId || null, content: '' };
      let ticker = null;
      let streamEnded = false;
      let drainResolver = null;
      let aborted = false;
      let firstTokenAt = 0;
      let startTimer = null;
      let lastReleaseAt = 0;

      // Per-request AbortController. handleStopRequest aborts this to:
      //   1. cancel the underlying fetch (backend sees req.on('close') and
      //      stops the LangGraph pipeline),
      //   2. trip the ticker's `aborted` flag so buffered tokens don't keep
      //      streaming in over the "Request stopped" message.
      const abortController = new AbortController();
      streamAbortRef.current = abortController;
      const onAbort = () => {
        aborted = true;
        streamEnded = true;
        pendingChars = '';
        if (ticker) { cancelAnimationFrame(ticker); ticker = null; }
        if (startTimer) { clearTimeout(startTimer); startTimer = null; }
        if (drainResolver) {
          const r = drainResolver;
          drainResolver = null;
          r();
        }
        if (streamingTurnRef.current && streamingTurnRef.current.aiMessageId === aiMessageId) {
          streamingTurnRef.current.completed = true;
          streamingTurnRef.current.isStreaming = false;
        }
      };
      abortController.signal.addEventListener('abort', onAbort, { once: true });

      // Auto-close common unclosed markdown tokens so the in-progress answer
      // doesn't visually flip when the closing delimiter finally arrives.
      const normalizePartialMarkdown = (text) => {
        let out = text;
        const triples = (out.match(/```/g) || []).length;
        if (triples % 2 === 1) out += '\n```';
        const bolds = (out.match(/\*\*/g) || []).length;
        if (bolds % 2 === 1) out += '**';
        // Safari < 16.4 does not support regex lookbehinds. Use match and filter instead.
        const inline = (out.match(/`+/g) || []).filter(m => m.length === 1).length;
        if (inline % 2 === 1) out += '`';
        return out;
      };

      const applyContent = (raw, { isFinal } = {}) => {
        const content = isFinal ? raw : normalizePartialMarkdown(raw);
        // Keep the ref in sync so handleStopRequest can grab the exact text
        // the user is currently looking at and send it to the stop endpoint.
        displayedContentRef.current = {
          conversationId: displayedContentRef.current.conversationId,
          content: raw
        };

        if (streamingTurnRef.current && streamingTurnRef.current.aiMessageId === aiMessageId) {
          streamingTurnRef.current.content = content;
          streamingTurnRef.current.isStreaming = !isFinal;
          streamingTurnRef.current.isThinking = false;
          streamingTurnRef.current.showReasoningIndicator = false;
        }
        const updater = (msg) => (msg.id === aiMessageId
          ? {
              ...msg,
              content,
              isStreaming: !isFinal,
              isThinking: false,
              showReasoningIndicator: false
            }
          : msg);
        setMessages(prev => prev.map(updater));
      };

      // ChatGPT-style word-at-a-time reveal. We release whole words (up to the
      // next whitespace) on a cadence aligned with the browser's paint cycle.
      // Using `requestAnimationFrame` instead of `setInterval` guarantees each
      // state update happens right before a repaint, eliminating mid-frame
      // commits that cause visible stutter.
      //
      // Base rate: one word per ~60ms ≈ 16-17 words/sec (comfortable reading
      // speed, close to ChatGPT). Adaptive to ~30ms when the backlog builds
      // so we catch up instead of finishing visibly after the network stream.
      const BASE_INTERVAL_MS = 60;
      const takeOneWord = () => {
        if (pendingChars.length === 0) return '';
        let i = 0;
        // Eat any leading whitespace so it travels with the word it precedes.
        while (i < pendingChars.length && /\s/.test(pendingChars[i])) i++;
        // Eat the word.
        while (i < pendingChars.length && !/\s/.test(pendingChars[i])) i++;
        // Eat one trailing whitespace char if present so the next tick starts
        // clean against the following word.
        if (i < pendingChars.length && /\s/.test(pendingChars[i])) i++;

        if (i === 0) return '';

        // If we hit the end of the buffer without a whitespace boundary AND the
        // stream is still active, the current trailing chars may be a
        // half-finished word (e.g. "**bo" awaiting "ld**"). Hold for more tokens
        // rather than emit a partial word — this is what stops markdown from
        // flipping layout mid-word.
        if (
          !streamEnded &&
          i === pendingChars.length &&
          pendingChars.length < 80 &&
          !/\s/.test(pendingChars[pendingChars.length - 1])
        ) {
          return '';
        }

        const take = pendingChars.slice(0, i);
        pendingChars = pendingChars.slice(i);
        return take;
      };

      // Jitter-buffer: wait this long after the first token before we start
      // revealing anything. This 200ms of accumulated buffer absorbs most
      // inter-chunk pauses from OpenAI so the reveal feels continuous instead
      // of "stream, pause, stream, pause".
      const STARTUP_BUFFER_MS = 200;
      // Keep at least this many chars in flight while the stream is still
      // active; skipping ticks when we're below this floor hides OpenAI pauses.
      const MIN_BUFFER_CHARS = 12;

      const startTicker = () => {
        if (ticker || startTimer) return;
        const kick = () => {
          startTimer = null;
          if (ticker) return;

          const frame = (now) => {
            // Stream ran dry AND network is done → we're fully drained
            if (pendingChars.length === 0) {
              if (streamEnded) {
                ticker = null;
                if (drainResolver) {
                  const r = drainResolver;
                  drainResolver = null;
                  r();
                }
                return;
              }
              // Waiting for more tokens — keep the frame loop alive
              ticker = requestAnimationFrame(frame);
              return;
            }

            // While the stream is still active, refuse to drain below the
            // floor — this is what masks OpenAI's mid-stream pauses.
            if (!streamEnded && pendingChars.length < MIN_BUFFER_CHARS) {
              ticker = requestAnimationFrame(frame);
              return;
            }

            // Adaptive target interval: tighter if the backlog is deep so we
            // don't finish visibly after the network stream ends.
            const backlog = pendingChars.length;
            const targetInterval =
              backlog > 1800 ? 25 :
              backlog > 600 ? 40 :
              BASE_INTERVAL_MS;

            if (now - lastReleaseAt < targetInterval) {
              ticker = requestAnimationFrame(frame);
              return;
            }

            const word = takeOneWord();
            if (word) {
              streamedContent += word;
              applyContent(streamedContent);
              lastReleaseAt = now;
            }
            ticker = requestAnimationFrame(frame);
          };

          ticker = requestAnimationFrame(frame);
        };

        const waited = Date.now() - (firstTokenAt || Date.now());
        if (waited >= STARTUP_BUFFER_MS) {
          kick();
        } else {
          startTimer = setTimeout(kick, STARTUP_BUFFER_MS - waited);
        }
      };

      const waitForDrain = () => new Promise((resolve) => {
        streamEnded = true;
        // If the jitter-buffer startup timer is still pending when the network
        // stream finishes, kick the ticker immediately so buffered chars drain.
        if (startTimer) {
          clearTimeout(startTimer);
          startTimer = null;
          startTicker();
        }
        if (pendingChars.length === 0 && !ticker) {
          resolve();
          return;
        }
        drainResolver = resolve;
      });

      const flushBufferImmediately = () => {
        if (pendingChars.length === 0) return;
        streamedContent += pendingChars;
        pendingChars = '';
        applyContent(streamedContent);
        lastReleaseAt = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
      };
      const handleVisibilityChange = () => {
        if (document.hidden) {
          if (ticker) { cancelAnimationFrame(ticker); ticker = null; }
          if (startTimer) { clearTimeout(startTimer); startTimer = null; }
          flushBufferImmediately();
        } else if (!streamEnded || pendingChars.length > 0) {
          startTicker();
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);
      cleanupVisibilityListener = () => {
        try { document.removeEventListener('visibilitychange', handleVisibilityChange); } catch { /* noop */ }
      };

      try {
        apiResult = await aiChatService.sendAgentMessageStream({
          message,
          context,
          conversationId,
          startDate,
          endDate,
          comparisonStartDate,
          comparisonEndDate,
          mode: activeTab === 1 ? 'clinical' : 'trend',
          signal: abortController.signal,
          onStage: ({ label }) => {
            // Show the current stage as a single-line message body. When
            // token streaming begins, applyContent() overwrites this with
            // the streamed answer.
            if (streamingTurnRef.current && streamingTurnRef.current.aiMessageId === aiMessageId) {
              streamingTurnRef.current.stageLabel = label;
            }
            const updater = (msg) => (msg.id === aiMessageId
              ? { ...msg, content: label }
              : msg);
            setMessages(prev => prev.map(updater));
          },
          onToken: (text) => {
            if (!firstTokenAt) firstTokenAt = Date.now();
            pendingChars += text;
            if (document.hidden) {
              flushBufferImmediately();
            } else {
              startTicker();
            }
          },
          onConversation: (convId) => {
            if (convId) {
              if (streamingTurnRef.current && streamingTurnRef.current.aiMessageId === aiMessageId) {
                streamingTurnRef.current.conversationId = convId;
              }

              if (!conversationIdRef.current || conversationIdRef.current === convId) {
                setConversationId(convId);
              }
              displayedContentRef.current = {
                ...displayedContentRef.current,
                conversationId: convId
              };
            }
          }
        });

        // Network stream is done; drain remaining buffered chars on screen
        // before we swap in the canonical final message below.
        await waitForDrain();
      } catch (streamErr) {
        if (ticker) { cancelAnimationFrame(ticker); ticker = null; }
        if (startTimer) { clearTimeout(startTimer); startTimer = null; }
        if (aborted || streamErr?.name === 'AbortError' || abortController.signal.aborted) {
          streamAbortRef.current = null;
          return;
        }
        if (streamErr?.isRateLimit) {
          throw streamErr;
        }
        throw streamErr;
      }

      // If the user aborted between the stream completing and us building
      // the final message, bail out without overwriting the "stopped" UI.
      if (aborted || abortController.signal.aborted) {
        streamAbortRef.current = null;
        return;
      }
      streamAbortRef.current = null;

      // Extract data from API result (mapAgentResponse wraps in { success, data })
      const result = apiResult?.data || apiResult;

      // Update the final AI message with complete data
      // For notes analysis tab, skip action buttons since actions are blocked
      // 🚨 CRITICAL: ALWAYS filter out actions for Clinical Analysis tab (activeTab === 1)
      const isClinicalAnalysis = activeTab === 1;

      // Debug logging for action filtering
      if (isClinicalAnalysis && (result.availableActions?.length > 0)) {
        console.warn('⚠️ [AiChatDrawer] Clinical Analysis tab received actions - FILTERING THEM OUT:', {
          availableActions: result.availableActions?.length || 0,
          activeTab,
          isClinicalAnalysis
        });
      }

      const finalAiMessage = {
        id: aiMessageId,
        type: 'ai',
        content: result.response || 'I apologize, but I couldn\'t generate a response.',
        timestamp: new Date(),
        suggestions: [],
        insights: [],
        // 🚨 CRITICAL: NEVER show actions in Clinical Analysis tab
        actions: isClinicalAnalysis ? [] : (result.availableActions || []),
        hasActions: isClinicalAnalysis ? false : ((result.availableActions && result.availableActions.length > 0) || false),
        // 🚨 CRITICAL: NEVER show executed actions in Clinical Analysis tab
        executedActions: [],
        hasExecutedActions: false,
        isStreaming: false,
        isThinking: false,
        showReasoningIndicator: false, // Hide reasoning indicator when complete
        // Flag validation messages so they don't persist in conversation history
        isValidationMessage: false,
        // Track reasoning mode used for this response (always precision for tool-based queries)
        reasoningMode: 'precision',
        reasoningModeLabel: reasoningModes.precision.label,
        processingTime: result.metadata?.processingTime?.totalTime || null,
        // Include metadata from AI agent — preserve the mode this turn ran in
        // so per-message mode tagging survives even if the backend omits it.
        metadata: { ...(result.metadata || {}), mode: result.metadata?.mode || messageMode }
      };

      setMessages(prev => prev.map(msg =>
        msg.id === aiMessageId ? finalAiMessage : msg
      ));

      if (streamingTurnRef.current && streamingTurnRef.current.aiMessageId === aiMessageId) {
        streamingTurnRef.current.content = finalAiMessage.content;
        streamingTurnRef.current.isStreaming = false;
        streamingTurnRef.current.isThinking = false;
        streamingTurnRef.current.showReasoningIndicator = false;
        streamingTurnRef.current.completed = true;
        if (result.conversationId) {
          streamingTurnRef.current.conversationId = result.conversationId;
        }
      }

      if (result.conversationId
          && (!conversationIdRef.current
              || conversationIdRef.current === result.conversationId)) {
        setConversationId(result.conversationId);
      }

      // Refresh usage stats after successful message (only if not SUPER user - SUPER users have unlimited access)
      if (auth?.role?.slug !== AUTH_ROLES.SUPER) {
        await refreshUsageStats();
      }

      // 🔔 If drawer is minimized when response arrives, show notification (badge + sound)
      // This allows users to: send message → minimize while waiting → get notified
      // IMPORTANT: Use ref to get CURRENT minimized state, not closure value
      const isCurrentlyMinimized = isMinimizedRef.current;

      if (isCurrentlyMinimized) {
        setUnreadCount(prev => {
          const newCount = prev + 1;
          return newCount;
        });
        playNotificationSound(); // Play sound to alert user
      }
    } catch (err) {

      // Check if the error is due to user abort
      if (err.message === 'Request aborted by user') {
        // Don't show error for user-initiated abort
        setError(null);
        // The thinking message should already be updated by handleStopRequest
        return;
      }

      setError(err.message || 'Failed to get AI response');

      // Update the streaming message with error
      // Check if error is about usage limit exceeded
      const isLimitError = err.message?.includes('limit') || err.message?.includes('Usage limit') || err.message?.includes('🚫');
      const errorMessage = {
        id: aiMessageId,
        type: 'ai',
        content: isLimitError
          ? err.message // Use the detailed limit error message from service
          : "I'm sorry, I'm having trouble responding right now. Please try again later.",
        timestamp: new Date(),
        isError: true,
        isStreaming: false,
        isThinking: false,
        showReasoningIndicator: false,
        metadata: { mode: messageMode }
      };

      setMessages(prev => prev.map(msg =>
        msg.id === aiMessageId ? errorMessage : msg
      ));

      if (streamingTurnRef.current && streamingTurnRef.current.aiMessageId === aiMessageId) {
        streamingTurnRef.current.content = errorMessage.content;
        streamingTurnRef.current.isStreaming = false;
        streamingTurnRef.current.isThinking = false;
        streamingTurnRef.current.showReasoningIndicator = false;
        streamingTurnRef.current.completed = true;
      }
    } finally {
      // Always set loading to false when request completes (success or error)
      // The handleStopRequest function already handles the abort case
      if (cleanupVisibilityListener) cleanupVisibilityListener();
      setIsLoading(false);
      sendInProgressRef.current = false;
    }
  }, [inputMessage, isLoading, messages, activeTab, pageType, activeFacilities, auth, conversationId, vectorStoreId, createNotesContext, reasoningModes, playNotificationSound, refreshUsageStats, filter, comparisonRanges, rangesSet]);

  // Initialize conversation with welcome message
  const initializeChat = useCallback(() => {
    if (open && messages.length === 0) {
      const supportsClinical = CLINICAL_TAB_PAGES.has(pageType?.toUpperCase());
      const welcomeMessage = {
        id: Date.now(),
        type: 'ai',
        content: supportsClinical
          ? `Hello! I'm your AI assistant for the ${pageType} dashboard. I can help you understand your data, identify trends, and provide insights. Switch between Trend Analysis and Clinical Analysis any time — your conversation stays in one thread.`
          : `Hello! I'm your AI assistant for the ${pageType} dashboard. I can help you understand your data, identify trends, and provide insights.`,
        timestamp: new Date(),
        isWelcome: true
      };
      setMessages([welcomeMessage]);

      // If there's an initial message, add it
      if (initialMessage) {
        handleSendMessage(initialMessage);
      }
    }
  }, [open, pageType, initialMessage, handleSendMessage, messages.length]);

  useEffect(() => {
    initializeChat();
  }, [initializeChat]);

  // Auto-scroll to bottom when new messages arrive — but only when the user
  // is already near the bottom. While streaming, setMessages() fires ~25x/sec;
  // without this gate, smooth-scroll would hijack any attempt to scroll up.
  useEffect(() => {
    if (!stickToBottomRef.current) return;
    scrollToBottom();
  }, [messages]);

  // Scroll listener installer — querying the DOM directly is more reliable
  // than a ref here, because `ChatContainer` is nested inside MUI's TabPanel
  // (Chat/History tabs) and a Fade transition, and the callback-ref approach
  // was being swallowed somewhere in the tree. We re-run whenever the tab
  // structure could have swapped the DOM node.
  useEffect(() => {
    if (!open || isMinimized) return undefined;

    let attached = null;
    let timer = null;

    const install = (el) => {
      if (!el || attached === el) return;
      attached = el;

      const ENABLE_PX = 20;
      const NAV_KEYS = new Set([
        'PageUp', 'PageDown', 'ArrowUp', 'ArrowDown', 'Home', 'End', ' '
      ]);

      // Direction-based detection. We can't rely on distance alone because
      // during streaming the auto-scroll snaps the user back to the bottom
      // faster than they can drag past any distance threshold — so the
      // flag never flips. Instead, we watch for any upward motion
      // (deltaY<0 on wheel, upward touch drag, scrollTop decreasing) and
      // disable auto-follow IMMEDIATELY on the first such event.
      let lastScrollTop = el.scrollTop;
      let lastTouchY = null;

      const disableStick = () => {
        stickToBottomRef.current = false;
      };
      const maybeEnableStick = () => {
        const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
        if (dist <= ENABLE_PX) stickToBottomRef.current = true;
      };

      const onWheel = (e) => {
        if (e.deltaY < 0) {
          // Wheel rolled upward → user wants to read earlier content
          disableStick();
        } else if (e.deltaY > 0) {
          // Wheel rolled downward → re-engage if they're back near bottom
          maybeEnableStick();
        }
      };
      const onTouchStart = (e) => {
        lastTouchY = e.touches[0]?.clientY ?? null;
      };
      const onTouchMove = (e) => {
        const y = e.touches[0]?.clientY;
        if (y == null) return;
        if (lastTouchY != null) {
          // Dragging finger DOWN on screen means scrolling UP through content
          const draggedDown = y > lastTouchY;
          if (draggedDown) disableStick();
          else maybeEnableStick();
        }
        lastTouchY = y;
      };
      const onTouchEnd = () => { lastTouchY = null; };
      const onKeyDown = (e) => {
        if (!NAV_KEYS.has(e.key)) return;
        // PageUp / ArrowUp / Home → treat as upward intent immediately.
        if (e.key === 'PageUp' || e.key === 'ArrowUp' || e.key === 'Home') {
          disableStick();
        } else {
          setTimeout(maybeEnableStick, 0);
        }
      };
      const onScroll = () => {
        if (el.scrollTop < lastScrollTop - 1) {
          disableStick();
        } else {
          maybeEnableStick();
        }
        lastScrollTop = el.scrollTop;
      };

      el.addEventListener('wheel', onWheel, { passive: true });
      el.addEventListener('touchstart', onTouchStart, { passive: true });
      el.addEventListener('touchmove', onTouchMove, { passive: true });
      el.addEventListener('touchend', onTouchEnd, { passive: true });
      el.addEventListener('keydown', onKeyDown);
      el.addEventListener('scroll', onScroll, { passive: true });

      chatContainerElRef.current = el;
      detachScrollListenersRef.current = () => {
        el.removeEventListener('wheel', onWheel);
        el.removeEventListener('touchstart', onTouchStart);
        el.removeEventListener('touchmove', onTouchMove);
        el.removeEventListener('touchend', onTouchEnd);
        el.removeEventListener('keydown', onKeyDown);
        el.removeEventListener('scroll', onScroll);
      };
    };

    const tryFind = () => {
      const el = document.querySelector('[data-ai-chat-scroll-container]');
      if (el) install(el);
      else timer = setTimeout(tryFind, 100);
    };
    tryFind();

    return () => {
      if (timer) clearTimeout(timer);
      if (detachScrollListenersRef.current) {
        detachScrollListenersRef.current();
        detachScrollListenersRef.current = null;
      }
      chatContainerElRef.current = null;
    };
  }, [open, isMinimized, activeAssistantTab]);

  // Scroll to bottom when drawer opens or is maximized
  useEffect(() => {
    if (open && !isMinimized) {
      setTimeout(() => {
        scrollToBottom({ force: true });
      }, 100); // Small delay to ensure DOM is rendered
    }
  }, [open, isMinimized]);

  // Notify parent when minimize state changes
  useEffect(() => {
    if (onMinimizeStateChange) {
      onMinimizeStateChange(isMinimized);
    }
  }, [isMinimized, onMinimizeStateChange]);

  // 🔔 Keep ref in sync with state for notification logic
  useEffect(() => {
    isMinimizedRef.current = isMinimized;
  }, [isMinimized]);

  const handleDrawerClose = () => {
    setIsClosing(false);
    setError(null);
    setInputMessage('');
    setUnreadCount(0);
    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    return () => {
      if (streamAbortRef.current) {
        try { streamAbortRef.current.abort(); } catch { /* noop */ }
        streamAbortRef.current = null;
      }
      aiChatService.abort();
    };
  }, []);

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleStopRequest = () => {
    // Abort the in-flight SSE fetch AND the RAF word-ticker. The signal's
    // 'abort' handler inside handleSendMessage clears the ticker, drops any
    // buffered characters, and resolves the drain promise so the outer
    // handleSendMessage flow exits cleanly. The backend observes the fetch
    // disconnect (req.on('close')) and stops the LangGraph pipeline.
    // Snapshot what the user currently sees BEFORE we abort — the ticker is
    // torn down synchronously by the abort handler, so this is our last
    // chance to capture the authoritative "what was on screen" content.
    const snapshot = { ...displayedContentRef.current };

    if (streamAbortRef.current) {
      try { streamAbortRef.current.abort(); } catch { /* noop */ }
      streamAbortRef.current = null;
    }
    aiChatService.abort();
    setIsLoading(false);

    if (streamingTurnRef.current) {
      streamingTurnRef.current.completed = true;
      streamingTurnRef.current.isStreaming = false;
      streamingTurnRef.current.isThinking = false;
      streamingTurnRef.current.content = snapshot.content || streamingTurnRef.current.content;
    }

    // Tell the backend to overwrite its saved assistant message with the
    // exact text the frontend had displayed at stop time. This is what the
    // user will see when they reopen the conversation from History.
    // Fire-and-forget; errors are logged inside recordStoppedReply.
    if (snapshot.conversationId) {
      aiChatService.recordStoppedReply(snapshot);
    }

    // Finalize the in-flight AI message without showing any "stopped" notice.
    // - If partial streaming content already arrived, keep it so the user can
    //   still read/copy what was generated.
    // - If only a stage label (or nothing) was on screen, drop the message
    //   entirely — an empty assistant row would look broken.
    const finalizeStopped = (list) => list.flatMap((msg) => {
      if (!(msg.isThinking || msg.isStreaming)) return [msg];
      const trimmed = typeof msg.content === 'string' ? msg.content.trim() : '';
      const hadPartial = trimmed.length > 0 && !STAGE_LABELS.has(trimmed);
      if (!hadPartial) return []; // remove placeholder / stage-label-only message
      return [{
        ...msg,
        isStreaming: false,
        isThinking: false,
        isStopped: true,
        showReasoningIndicator: false
      }];
    });
    setMessages(finalizeStopped);
  };


  const handleCopyMessage = (content, messageId) => {
    navigator.clipboard.writeText(content);
    setCopiedMessageId(messageId);
    setTimeout(() => {
      setCopiedMessageId((current) => (current === messageId ? null : current));
    }, 1500);
  };

  // eslint-disable-next-line no-unused-vars -- Available Actions UI commented out
  const handleExecuteAction = async (action) => {
    try {
      // Handle special tab switching action for Clinical Analysis mode
      if (action.type === 'switch_to_regular_chat' && action.isNotesAnalysisAction) {

        // Switch to Trend Analysis tab (index 0)
        setActiveTab(0);

        // Add a message explaining the switch
        const switchMessage = {
          id: Date.now(),
          type: 'ai',
          content: `🔄 Switched to **Trend Analysis tab**. You can now use dashboard actions like charts, reports, filters, and patient lists.
          
💡 Feel free to repeat your request here to execute the action.`,
          timestamp: new Date(),
          isActionResult: true
        };
        setMessages(prev => [...prev, switchMessage]);

        return;
      }

      // Show loading state in the appropriate tab
      const loadingMessage = {
        id: Date.now(),
        type: 'ai',
        content: `⏳ Executing action: ${action.label || action.buttonText || action.description}...`,
        timestamp: new Date(),
        isActionResult: true
      };
      setMessages(prev => [...prev, loadingMessage]);

      const result = await aiChatService.executeAction(action);

      if (result.success) {
        const successMessage = {
          id: Date.now(),
          type: 'ai',
          content: `✅ ${result.message}`,
          timestamp: new Date(),
          isActionResult: true
        };

        setMessages(prev => [...prev, successMessage]);

        // If the result includes continueConversation, send it as a new message
        if (result.continueConversation) {
          setTimeout(() => {
            handleSendMessage(result.continueConversation);
          }, 500);
        }

        // Add specific feedback for different action types
        let refreshMessage = null;
        if (action.type === 'apply_filter' || action.type === 'filter_action') {
          refreshMessage = {
            id: Date.now() + 1,
            type: 'ai',
            content: `🔄 Filter applied to dashboard. Data is refreshing...`,
            timestamp: new Date(),
            isActionResult: true
          };
        } else if (action.type === 'census_action') {
          refreshMessage = {
            id: Date.now() + 1,
            type: 'ai',
            content: `📊 Percentage calculation method changed. All cards are updating...`,
            timestamp: new Date(),
            isActionResult: true
          };
        } else if (action.type === 'date_action') {
          refreshMessage = {
            id: Date.now() + 1,
            type: 'ai',
            content: `📅 Date range changed. Loading data for the new period...`,
            timestamp: new Date(),
            isActionResult: true
          };
        }

        if (refreshMessage) {
          setMessages(prev => [...prev, refreshMessage]);
        }
      } else {
        const errorMessage = {
          id: Date.now(),
          type: 'ai',
          content: `❌ Action failed: ${result.error}`,
          timestamp: new Date(),
          isActionResult: true
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      const errorMessage = {
        id: Date.now(),
        type: 'ai',
        content: `❌ Action execution failed: ${error.message}`,
        timestamp: new Date(),
        isActionResult: true
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  };


  const handleClearChat = () => {
    if (streamAbortRef.current) {
      try { streamAbortRef.current.abort(); } catch { /* noop */ }
      streamAbortRef.current = null;
    }
    streamingTurnRef.current = null;
    setIsLoading(false);

    setActiveAssistantTab('chat');
    setMessages([]);
    setConversationId(null);
    setError(null);
    setLoadedConversationDateRange(null);
    sendInProgressRef.current = false;

    const supportsClinical = CLINICAL_TAB_PAGES.has(pageType?.toUpperCase());
    const welcomeMessage = {
      id: Date.now(),
      type: 'ai',
      content: supportsClinical
        ? `Hello! I'm your AI assistant for the ${pageType} dashboard. I can help you understand your data, identify trends, and provide insights. Switch between Trend Analysis and Clinical Analysis any time — your conversation stays in one thread.`
        : `Hello! I'm your AI assistant for the ${pageType} dashboard. I can help you understand your data, identify trends, and provide insights.`,
      timestamp: new Date(),
      isWelcome: true
    };
    setMessages([welcomeMessage]);
  };

  // eslint-disable-next-line no-unused-vars
  const handleMinimize = () => {
    setIsMinimized(true);
  };

  const handleMaximize = () => {
    setIsMinimized(false);
    setUnreadCount(0); // Clear unread count when maximized

    // Scroll to bottom to show latest messages when drawer is opened
    setTimeout(() => {
      scrollToBottom({ force: true });
    }, 100); // Small delay to ensure DOM is updated
  };

  const handleTabChange = (event, newValue) => {
    // 🚨 CRITICAL: Prevent mode switching while AI is processing a request
    // so the in-flight reply is finalized under the mode it was sent under.
    if (isLoading) {
      console.warn('⚠️ Cannot switch modes while AI request is in progress');
      return;
    }

    // Mode is just a setting on the *next* outgoing message — the conversation
    // (messages list) is shared across both modes, so we don't reset anything.
    // Preserve whatever the user has typed; switching modes shouldn't discard their draft.
    setActiveTab(newValue);
  };


  const formatDateRange = () => {
    // Always reflect the dashboard's currently selected range; fall back to the
    // loaded conversation's stored range only when the dashboard filter is unset.
    const start = filter?.startDate || loadedConversationDateRange?.startDate;
    const end = filter?.endDate || loadedConversationDateRange?.endDate;
    if (!start || !end) return null;

    return `${moment(start).format('MMM DD, YYYY')} - ${moment(end).format('MMM DD, YYYY')}`;
  };


  // Initialize AI services with report and alert functions
  React.useEffect(() => {
    if (handleOnClickReport && downloadExcel) {
      // Initialize AI chat service with report functions
      aiChatService.initializeReportService(handleOnClickReport, downloadExcel);
    }
  }, [handleOnClickReport, downloadExcel]);

  const isNewChat = !isLoading
    && messages.length <= 1
    && (messages.length === 0 || messages[0]?.isWelcome);
  const newChatSuggestions = getNewChatSuggestions(pageType, activeTab);
  const pageLabel = PAGE_LABEL[pageType?.toUpperCase()] || 'Dashboard';
  const inputPlaceholder = isLimitExceeded
    ? 'Prompt limit exceeded for this month'
    : (loadingMessage || (activeTab === 0
        ? 'Ask me anything about your data...'
        : 'Ask me to analyze patient data...'));

  return (
    <>
      {/* Floating Chat Button when minimized */}
      {open && isMinimized && (
        <MinimizedButton onClick={handleMaximize}>
          <Box sx={{ position: 'relative' }}>
            <ChatIcon />
            {unreadCount > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  backgroundColor: 'error.main',
                  borderRadius: '50%',
                  width: 12,
                  height: 12,
                  border: '2px solid white',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}
              />
            )}
          </Box>
        </MinimizedButton>
      )}

      {/* Only show drawer when not minimized */}
      {!isMinimized && (
        <StyledDrawer
          anchor="right"
          open={open}
          onClose={handleDrawerClose}
          variant="persistent"
          isExpanded={isExpanded}
        >
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <AppBar position="static" color="primary" elevation={0}>
              <Toolbar sx={{ minHeight: 56, flexDirection: 'column', alignItems: 'stretch', py: 1, justifyContent: 'center', gap: { xs: 1.4, sm: 1 } }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" gap={1}>
                  <Box display="flex" alignItems="center" gap={1} sx={{ flex: '1 1 0', minWidth: 0 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'white', color: 'primary.main', flexShrink: 0 }}>
                      <AIIcon fontSize="small" />
                    </Avatar>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
                      <Typography variant="h6" component="div" color="white" sx={{ lineHeight: 1, whiteSpace: 'nowrap' }}>
                        AI Assistant
                      </Typography>
                      {formatDateRange() && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, height: '100%' }}>
                          <Typography variant="caption" color="rgba(255, 255, 255, 0.9)" sx={{ fontWeight: 500, lineHeight: 1, whiteSpace: 'nowrap' }}>
                            {formatDateRange()}
                          </Typography>
                          <ClickAwayListener onClickAway={() => setDateInfoTooltipOpen(false)}>
                            <Tooltip
                              title={
                                activeTab === 0
                                  ? 'AI will provide trend analysis and insights for this date range'
                                  : 'AI will provide clinical analysis with comprehensive field access for this date range'
                              }
                              arrow
                              open={dateInfoTooltipOpen}
                              onClose={() => setDateInfoTooltipOpen(false)}
                              onOpen={() => setDateInfoTooltipOpen(true)}
                              disableFocusListener
                            >
                              <InfoOutlinedIcon
                                onClick={() => setDateInfoTooltipOpen((prev) => !prev)}
                                sx={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.85)', cursor: 'pointer' }}
                              />
                            </Tooltip>
                          </ClickAwayListener>
                        </Box>
                      )}
                      
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="center" gap={0.5} sx={{ flexShrink: 0 }}>
                    {auth?.role?.slug !== AUTH_ROLES.SUPER && (
                      <AiUsageStats
                        usageStats={usageStats}
                        loading={usageLoading}
                        sx={{ display: { xs: 'none', sm: 'flex' }, mr: 1 }}
                      />
                    )}
                    <IconButton
                      size="small"
                      onClick={handleClearChat}
                      sx={{
                        color: 'white',
                        borderRadius: 0.6,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.2,
                        px: 1.2,
                        py: 0.7,
                        position: 'relative',
                        overflow: 'hidden',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        isolation: 'isolate',
                        '& > *': { position: 'relative', zIndex: 2 },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          width: '300%',
                          height: '300%',
                          transform: 'translate(-50%, -50%)',
                          background: 'conic-gradient(transparent 0deg, transparent 270deg, rgba(255,255,255,0.95) 320deg, rgba(255,255,255,0.95) 360deg)',
                          animation: 'newChatBorderSpin 2.5s linear infinite',
                          zIndex: 0,
                          pointerEvents: 'none',
                        },
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          inset: '1px',
                          borderRadius: 'inherit',
                          backgroundColor: (theme) => theme.palette.primary.main,
                          zIndex: 1,
                          pointerEvents: 'none',
                        },
                        '@keyframes newChatBorderSpin': {
                          from: { transform: 'translate(-50%, -50%) rotate(0deg)' },
                          to: { transform: 'translate(-50%, -50%) rotate(360deg)' },
                        },
                      }}
                    >
                      <AddIcon fontSize="16px" sx={{ color: 'white' }} />
                      <Typography variant="caption" color="white" sx={{ fontSize: '12px', fontWeight: 500 }}>
                        New chat
                      </Typography>
                    </IconButton>

                    <Tooltip title={isClosing ? "Closing..." : "Close"}>
                      <IconButton 
                        size="small" 
                        onClick={handleDrawerClose} 
                        disabled={isClosing}
                        sx={{ color: 'white' }}
                      >
                        {isClosing ? (
                          <CircularProgress size={16} sx={{ color: 'white' }} />
                        ) : (
                          <CloseIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                {auth?.role?.slug !== AUTH_ROLES.SUPER && (
                  <AiUsageStats
                    usageStats={usageStats}
                    loading={usageLoading}
                    fullWidth
                    sx={{ display: { xs: 'flex', sm: 'none' } }}
                  />
                )}
              </Toolbar>
            </AppBar>

            <TabContext value={activeAssistantTab}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <TabList onChange={handleAssistantTabChange} aria-label="lab API tabs example">
                  <Tab disableRipple label="Chat" value="chat" sx={{ width: '50%', maxWidth: '100%' }} />
                  <Tab disableRipple label="History" value="history" sx={{ width: '50%', maxWidth: '100%' }} />
                </TabList>
              </Box>
              <TabPanel value="chat" sx={{ padding: 0, height: '100%', minHeight: 0 }}>
            <Fade in timeout={250} key="chat-fade">
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {/* Content Area */}
            <Box sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              p: 1.5,
              minHeight: 0, // Important for flex child to shrink
              overflow: 'hidden', // Prevent content from overflowing
              height: '100%',
              backgroundColor: (theme) => theme.palette.grey[50]
            }}>

              {isNewChat ? (
                <Box sx={{
                  my: "auto",
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  overflow: 'auto',
                }}>
                  <Avatar sx={{
                    width: 56,
                    height: 56,
                    bgcolor: 'secondary.main',
                    mb: 0.5,
                  }}>
                    <AIIcon style={{ fontSize: 28 }} />
                  </Avatar>
                  <Typography sx={{
                    fontSize: '20px',
                    fontWeight: 600,
                    color: 'text.primary',
                    textAlign: 'center',
                  }}>
                    How can I help you today?
                  </Typography>
                  <Typography sx={{
                    fontSize: '13px',
                    color: 'text.secondary',
                    textAlign: 'center',
                    maxWidth: 480,
                    mb: 1,
                  }}>
                    AI assistant for the {pageLabel} dashboard. Ask about trends, breakdowns, or comparisons.
                  </Typography>

                  <Box position="relative" width="100%" sx={{ maxWidth: 560 }}>
                    <Box sx={{ width: '100%', border: isLimitExceeded ? '1px solid #ffc107' : '1px solid #e3e3e3', borderRadius: "12px", overflow: "hidden", minHeight: '82px', maxHeight: '160px' }} display="flex" alignItems="center">
                      <Box
                        component="textarea"
                        ref={inputRef}
                        value={inputMessage}
                        onChange={(e) => {
                          setInputMessage(e.target.value);
                          e.target.style.height = 'auto';
                          e.target.style.height = Math.min(e.target.scrollHeight, 140) + 'px';
                        }}
                        onKeyPress={handleKeyPress}
                        disabled={isLoading || disabled || isLimitExceeded}
                        placeholder={inputPlaceholder}
                        onFocus={(e) => {
                          if (!disabled && !isLimitExceeded) {
                            e.target.style.borderColor = '#1976d2';
                          }
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = isLimitExceeded ? '#ffc107' : '#e3e3e3';
                        }}
                        sx={{
                          width: '100%',
                          height: "100%",
                          minHeight: '82px',
                          maxHeight: '140px',
                          padding: '14px 14px 60px',
                          border: 0,
                          borderRadius: '0',
                          fontSize: '14px',
                          resize: 'none',
                          outline: 'none',
                          backgroundColor: (isLoading || disabled || isLimitExceeded) ? '#f5f5f5' : 'white',
                          color: (isLoading || disabled || isLimitExceeded) ? '#999' : 'inherit',
                          boxSizing: 'border-box',
                          lineHeight: '1.5',
                          overflow: 'auto',
                          cursor: (disabled || isLimitExceeded) ? 'not-allowed' : 'text',
                          fontFamily: 'manrope',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                          '&::placeholder': { fontSize: '13px' },
                          '&::-webkit-scrollbar': {
                            width: 8,
                          },
                          '&::-webkit-scrollbar-track': {
                            backgroundColor: (theme) => theme.palette.grey[100],
                            borderRadius: 4,
                          },
                          '&::-webkit-scrollbar-thumb': {
                            backgroundColor: (theme) => theme.palette.grey[400],
                            borderRadius: 4,
                            '&:hover': {
                              backgroundColor: (theme) => theme.palette.grey[600],
                            },
                          },
                          scrollbarWidth: 'thin',
                          scrollbarColor: (theme) => `${theme.palette.grey[400]} ${theme.palette.grey[100]}`,
                        }}
                      />
                    </Box>
                    <Box sx={{ position: "absolute", bottom: 1, left: 1, right: 1, display: "flex", justifyContent: "flex-end", alignItems: "center", backgroundColor: (isLoading || disabled || isLimitExceeded) ? '#f5f5f5' : 'white', p: "14px", gap: "8px", borderRadius: "12px" }}>
                      {CLINICAL_TAB_PAGES.has(pageType?.toUpperCase()) && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                          onClick={(e) => { if (!isLoading && !disabled) setModeAnchorEl(e.currentTarget); }}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: (isLoading || disabled) ? 'not-allowed' : 'pointer',
                            opacity: (isLoading || disabled) ? 0.4 : 0.6,
                            '&:hover': { opacity: (isLoading || disabled) ? 0.4 : 1, backgroundColor: (isLoading || disabled) ? 'transparent' : 'rgba(0,0,0,0.04)' },
                            fontSize: '12px',
                            color: 'text.secondary',
                            userSelect: 'none',
                            fontWeight: 600,
                            py: "7px",
                            px: "10px",
                            borderRadius: '6px',
                            fontFamily: 'manrope',
                          }}
                        >
                          {activeTab === 0
                            ? <><AnalyticsIcon sx={{ fontSize: 14 }} /> Trend Analysis</>
                            : <><PsychologyIcon sx={{ fontSize: 14 }} /> Clinical Analysis</>
                          }
                          <span style={{ marginLeft: 2, marginTop: 2 }}>
                            <KeyboardArrowDownIcon sx={{ width: "15px", height: "15px" }} />
                          </span>
                        </Box>
                      </Box>
                      )}
                      <Tooltip title={
                        isLimitExceeded
                          ? 'Prompt limit exceeded for this month'
                          : (disabled ? 'Data is loading...' : 'Send message')
                      }>
                        <span>
                          <IconButton
                            onClick={() => handleSendMessage()}
                            disabled={!inputMessage.trim() || disabled || isLimitExceeded || isLoading}
                            size="small"
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: 0.6,
                              backgroundColor: 'primary.main',
                              color: 'white',
                              opacity: isLimitExceeded ? 0.6 : 1,
                              '&:hover': { backgroundColor: 'primary.dark' },
                              '&.Mui-disabled': { backgroundColor: '#e0e0e0', color: '#9e9e9e' },
                            }}
                          >
                            <SendIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    gap: 1,
                    maxWidth: 560,
                    mt: 1,
                  }}>
                    {newChatSuggestions.map((s) => (
                      <Chip
                        key={s.label}
                        icon={<span style={{ fontSize: 14, marginLeft: 8 }}>{s.icon}</span>}
                        label={s.label}
                        variant="outlined"
                        onClick={() => {
                          if (!disabled && !isLimitExceeded && !isLoading) {
                            handleSendMessage(s.prompt);
                          }
                        }}
                        disabled={disabled || isLimitExceeded || isLoading}
                        sx={{
                          borderRadius: '16px',
                          height: 32,
                          fontSize: '12px',
                          backgroundColor: 'white',
                          borderColor: '#e3e3e3',
                          '&:hover': {
                            backgroundColor: 'rgba(72, 121, 245, 0.06)',
                            borderColor: 'primary.main',
                          },
                          '.MuiChip-label' : {
                            lineHeight: 1.2,
                          }
                        }}
                      />
                    ))}
                  </Box>

                  {error && (
                    <Alert severity="error" sx={{ mt: 1, maxWidth: 560, width: '100%' }} onClose={() => setError(null)}>
                      {error}
                    </Alert>
                  )}
                  {auth?.role?.slug !== AUTH_ROLES.SUPER && isLimitExceeded && (
                    <Alert
                      severity="warning"
                      sx={{
                        mt: 1,
                        maxWidth: 560,
                        width: '100%',
                        backgroundColor: '#fff3cd',
                        border: '1px solid #ffc107',
                        '& .MuiAlert-message': { color: '#856404', fontWeight: 500 },
                      }}
                    >
                      Prompt limit exceeded for this month. Your limit will reset at the start of next month.
                    </Alert>
                  )}
                </Box>
              ) : (
              <>
              {/* Chat Container */}
              <ChatContainer data-ai-chat-scroll-container>
                <MessageContainer>
                  {messages.filter(m => !m.isWelcome).map((message) => (
                    <MessageRow key={message.id} isUser={message.type === 'user'}>
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          bgcolor: message.type === 'user' ? 'primary.main' : 'secondary.main'
                        }}
                      >
                        {message.type === 'user' ? <UserIcon fontSize="small" /> : <AIIcon fontSize="small" />}
                      </Avatar>
                      <Box sx={{ maxWidth: '80%', minWidth: { xs: '20%', sm: '10%' } }}>
                        <MessageBubble isUser={message.type === 'user'} elevation={1}>
                          <Box sx={{
                            '& table': {
                              borderCollapse: 'collapse',
                              width: '100%',
                              mb: 2,
                              fontSize: '0.875rem'
                            },
                            '& th, & td': {
                              border: '1px solid #ddd',
                              padding: '8px',
                              textAlign: 'left'
                            },
                            '& th': {
                              backgroundColor: '#f5f5f5',
                              fontWeight: 600
                            },
                            '& h2': {
                              fontSize: '1.1rem',
                              fontWeight: 600,
                              mt: 2,
                              mb: 1
                            },
                            '& h3': {
                              fontSize: '1rem',
                              fontWeight: 600,
                              mt: 1.5,
                              mb: 0.5
                            },
                            '& ul, & ol': {
                              pl: 2,
                              mb: 1
                            },
                            '& li': {
                              mb: 0.5
                            },
                            '& p': {
                              mb: 1,
                              '&:last-child': {
                                mb: 0
                              }
                            },
                            '& strong': {
                              fontWeight: 600
                            },
                            '& code': {
                              backgroundColor: '#f5f5f5',
                              padding: '2px 4px',
                              borderRadius: '4px',
                              fontSize: '0.85em'
                            },
                            '& pre': {
                              backgroundColor: '#f5f5f5',
                              padding: '12px',
                              borderRadius: '4px',
                              overflow: 'auto',
                              '& code': {
                                backgroundColor: 'transparent',
                                padding: 0
                              }
                            },
                            '& hr': {
                              my: 2,
                              border: 'none',
                              borderTop: '1px solid #ddd'
                            },
                            '& blockquote': {
                              borderLeft: '3px solid #ddd',
                              pl: 2,
                              ml: 0,
                              color: 'text.secondary'
                            },
                            '& a': {
                              color: 'primary.main',
                              textDecoration: 'underline'
                            }
                          }}>
                            {message.type === 'ai' && message.isThinking
                              ? <> 🧠 <ShimmerText>Thinking...</ShimmerText> </>
                              : <MemoMarkdown content={message.content} />}
                          </Box>

                          {/* Display actionable suggestions as clickable chips */}
                          {message.suggestions && message.suggestions.length > 0 && (
                            <Box sx={{ mt: 0.8, display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                              {message.suggestions.map((suggestion, index) => {
                                const isString = typeof suggestion === 'string';
                                const text = isString ? suggestion : suggestion.text;
                                const priority = isString ? 'medium' : (suggestion.priority || 'medium');

                                return (
                                  <Chip
                                    key={index}
                                    label={text}
                                    size="small"
                                    variant="outlined"
                                    onClick={() => {
                                      setInputMessage(text);
                                      inputRef.current?.focus();
                                    }}
                                    sx={{
                                      cursor: 'pointer',
                                      fontSize: '0.65rem',
                                      height: 20,
                                      color: priority === 'high' ? 'error.main' :
                                        priority === 'medium' ? 'warning.main' : 'primary.main',
                                      borderColor: priority === 'high' ? 'error.main' :
                                        priority === 'medium' ? 'warning.main' : 'primary.main'
                                    }}
                                  />
                                );
                              })}
                            </Box>
                          )}
                          {/* Display actionable insights */}
                          {message.insights && message.insights.length > 0 && (
                            <Box sx={{ mt: 0.8 }}>
                              <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>
                                💡 Key Insights:
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mt: 0.4 }}>
                                {message.insights.slice(0, 3).map((insight, index) => (
                                  <Chip
                                    key={index}
                                    label={insight}
                                    size="small"
                                    variant="filled"
                                    color="secondary"
                                    sx={{
                                      fontSize: '0.6rem',
                                      height: 18,
                                      maxWidth: '180px'
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}

                          {/* Display executed actions - ONLY show in Trend Analysis tab (activeTab === 0) */}
                          {message.executedActions && message.executedActions.length > 0 && activeTab === 0 && (
                            <Box sx={{ mt: 0.8 }}>
                              <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>
                                ✅ Actions Executed:
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mt: 0.4 }}>
                                {message.executedActions.map((action, index) => (
                                  <Chip
                                    key={index}
                                    label={action.message || action.description}
                                    size="small"
                                    variant="filled"
                                    color="success"
                                    sx={{
                                      fontSize: '0.6rem',
                                      height: 20,
                                      maxWidth: '200px'
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}

                          {/* Display actionable buttons - ONLY show in Trend Analysis tab (activeTab === 0) */}
                          {/* 🚨 CRITICAL: Double-check activeTab to prevent showing actions in Clinical Analysis */}
                          {/*
                          {message.actions && message.actions.length > 0 && activeTab === 0 && !message.isValidationMessage && (
                            <Box sx={{ mt: 0.8 }}>
                              <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>
                                ⚡ Available Actions:
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mt: 0.4 }}>
                                {message.actions.map((action, index) => (
                                  <Chip
                                    key={index}
                                    label={action.label || action.buttonText || action.description}
                                    size="small"
                                    variant={action.isNotesAnalysisAction ? "filled" : "outlined"}
                                    onClick={() => handleExecuteAction(action)}
                                    sx={{
                                      cursor: 'pointer',
                                      fontSize: '0.6rem',
                                      height: 20,
                                      maxWidth: '200px',
                                      color: action.isNotesAnalysisAction ? 'white' : 'primary.main',
                                      borderColor: 'primary.main',
                                      backgroundColor: action.isNotesAnalysisAction ? 'primary.main' : 'transparent',
                                      fontWeight: action.isNotesAnalysisAction ? 'bold' : 'normal',
                                      '&:hover': {
                                        backgroundColor: action.isNotesAnalysisAction ? 'primary.dark' : 'primary.main',
                                        color: 'white'
                                      }
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                          */}
                        </MessageBubble>
                        <Box display="flex" justifyContent={message.type === 'user' ? 'flex-end' : 'flex-start'} alignItems="center" sx={{ mt: 0.5 }}>
                          {/* Hide timestamp while an AI reply is still streaming/thinking —
                              it should only appear once the answer is complete (or stopped),
                              matching the copy-icon visibility. */}
                          {!(message.type === 'ai' && (message.isStreaming || message.isThinking)) && (
                            <Typography variant="caption" color="textSecondary">
                              {message.timestamp.toLocaleTimeString()}
                            </Typography>
                          )}
                          {message.type === 'ai' && !message.isWelcome && !message.isStreaming && !message.isThinking && (
                            <Tooltip title={copiedMessageId === message.id ? 'Copied' : 'Copy message'}>
                              <IconButton
                                size="small"
                                onClick={() => handleCopyMessage(message.content, message.id)}
                                sx={{ ml: 1 }}
                              >
                                {copiedMessageId === message.id ? (
                                  <CheckIcon fontSize="18px" sx={{ color: 'success.main' }} />
                                ) : (
                                  <CopyIcon fontSize="18px" />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    </MessageRow>
                  ))}


                  <div ref={messagesEndRef} />
                </MessageContainer>
              </ChatContainer>

              {error && (
                <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {/* Limit Exceeded Message - Hide for SUPER users */}
              {auth?.role?.slug !== AUTH_ROLES.SUPER && isLimitExceeded && (
                <Alert
                  severity="warning"
                  sx={{
                    mt: 1.5,
                    mb: 1,
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107',
                    '& .MuiAlert-message': {
                      color: '#856404',
                      fontWeight: 500
                    }
                  }}
                >
                  Prompt limit exceeded for this month. Your limit will reset at the start of next month.
                </Alert>
              )}

              {/* Input Area */}
              <Box
                display="flex"
                width="100%"
                gap={1}
                sx={{
                  mt: 1.5,
                  flexShrink: 0, // Prevent input area from shrinking
                  zIndex: 1, // Ensure it stays on top
                  alignItems: 'center' // Center align buttons vertically
                }}
              >
                <Box sx={{ flex: 1, position: 'relative', width: '100%', display: "flex", overflow: "hidden", borderRadius: "8px" }}>
                  <Box
                    component="textarea"
                    ref={inputRef}
                    value={inputMessage}
                    onChange={(e) => {
                      setInputMessage(e.target.value);
                      // Auto-resize functionality
                      e.target.style.height = 'auto';
                      e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'; // Max 2 rows (30px per row)
                    }}
                    onKeyPress={handleKeyPress}
                    disabled={isLoading || disabled || isLimitExceeded}
                    placeholder={
                      isLimitExceeded
                        ? "Prompt limit exceeded for this month"
                        : (loadingMessage ||
                          (activeTab === 0 ? "Ask me anything about your data..." : "Ask me to analyze patient data..."))
                    }
                    onFocus={(e) => {
                      if (!disabled && !isLimitExceeded) {
                        e.target.style.borderColor = '#1976d2';
                      }
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = isLimitExceeded ? '#ffc107' : '#c4c4c4';
                    }}
                    sx={{
                      width: '100%',
                      minHeight: '36px',
                      maxHeight: '120px',
                      padding: '8px 8px 50px',
                      border: isLimitExceeded ? '1px solid #ffc107' : '1px solid #e3e3e3',
                      borderRadius: '8px',
                      fontSize: '14px',
                      resize: 'none',
                      outline: 'none',
                      backgroundColor: (isLoading || disabled || isLimitExceeded) ? '#f5f5f5' : 'white',
                      color: (isLoading || disabled || isLimitExceeded) ? '#999' : 'inherit',
                      boxSizing: 'border-box',
                      lineHeight: '1.4',
                      overflow: 'auto',
                      cursor: (disabled || isLimitExceeded) ? 'not-allowed' : 'text',
                      fontFamily: 'manrope',
                      '&::placeholder': {
                        fontSize: '12px',
                      },
                      '&::-webkit-scrollbar': {
                        width: 8,
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: (theme) => theme.palette.grey[100],
                        borderRadius: 4,
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: (theme) => theme.palette.grey[400],
                        borderRadius: 4,
                        '&:hover': {
                          backgroundColor: (theme) => theme.palette.grey[600],
                        },
                      },
                      scrollbarWidth: 'thin',
                      scrollbarColor: (theme) => `${theme.palette.grey[400]} ${theme.palette.grey[100]}`,
                    }}
                  />
                  <Box sx={{ position: "absolute", bottom: 1, left: 1, right: 1, display: "flex", justifyContent: "flex-end", alignItems: "center", backgroundColor: (isLoading || disabled || isLimitExceeded) ? '#f5f5f5' : 'white', p: "8px", gap: "8px", borderRadius: "8px" }}>
                    {CLINICAL_TAB_PAGES.has(pageType?.toUpperCase()) && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        onClick={(e) => { if (!isLoading && !disabled) setModeAnchorEl(e.currentTarget); }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          cursor: (isLoading || disabled) ? 'not-allowed' : 'pointer',
                          opacity: (isLoading || disabled) ? 0.4 : 0.6,
                          '&:hover': { opacity: (isLoading || disabled) ? 0.4 : 1, backgroundColor: (isLoading || disabled) ? 'transparent' : 'rgba(0,0,0,0.04)' },
                          fontSize: '11px',
                          color: 'text.secondary',
                          userSelect: 'none',
                          fontWeight: 600,
                          py: "6px",
                          px: "10px",
                          borderRadius: '6px',
                          fontFamily: 'manrope',
                        }}
                      >
                        {activeTab === 0
                          ? <><AnalyticsIcon sx={{ fontSize: 13 }} /> Trend Analysis</>
                          : <><PsychologyIcon sx={{ fontSize: 13 }} /> Clinical Analysis</>
                        }
                        <span style={{ marginLeft: 2, marginTop: 2 }}>
                              <KeyboardArrowDownIcon sx={{ width: "15px", height: "15px" }} />
                            </span>
                      </Box>
                    </Box>
                    )}
                    <Box>
                      {isLoading ? (
                        <Tooltip title="Stop AI request">
                          <IconButton
                            onClick={handleStopRequest}
                            size="small"
                            sx={{
                              width: 30,
                              height: 30,
                              borderRadius: 0.6,
                              backgroundColor: '#9e9e9e',
                              color: 'white',
                              '&:hover': { backgroundColor: '#757575' }
                            }}
                          >
                            <StopIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title={
                          isLimitExceeded
                            ? "Prompt limit exceeded for this month"
                            : (disabled ? "Data is loading..." : "Send message")
                        }>
                          <span>
                            <IconButton
                              onClick={() => handleSendMessage()}
                              disabled={!inputMessage.trim() || disabled || isLimitExceeded}
                              size="small"
                              sx={{
                                width: 30,
                                height: 30,
                                borderRadius: 0.6,
                                backgroundColor: 'primary.main',
                                color: 'white',
                                opacity: isLimitExceeded ? 0.6 : 1,
                                '&:hover': { backgroundColor: 'primary.dark' },
                                '&.Mui-disabled': { backgroundColor: '#e0e0e0', color: '#9e9e9e' }
                              }}
                            >
                              <SendIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
              </>
              )}

              {/* Mode selection menu (shared by landing + docked inputs) */}
              <Menu
                anchorEl={modeAnchorEl}
                open={Boolean(modeAnchorEl)}
                onClose={() => setModeAnchorEl(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                slotProps={{ paper: { sx: { borderRadius: '10px', minWidth: 190 } } }}
              >
                <MenuItem
                  selected={activeTab === 0}
                  onClick={() => { handleTabChange(null, 0); setModeAnchorEl(null); }}
                  sx={{ borderRadius: '6px', mx: "8px", my: 0.25, px: "8px" }}
                >
                  <ListItemIcon sx={{ minWidth: 'auto !important', mr: 1 }}><AnalyticsIcon fontSize="18px" /></ListItemIcon>
                  <ListItemText
                    primary="Trend Analysis"
                    secondary="Dashboard data & trends"
                    primaryTypographyProps={{ fontSize: '11px', fontWeight: 600 }}
                    secondaryTypographyProps={{ fontSize: '10px' }}
                  />
                </MenuItem>
                <MenuItem
                  selected={activeTab === 1}
                  onClick={() => { handleTabChange(null, 1); setModeAnchorEl(null); }}
                  sx={{ borderRadius: '6px', mx: "8px", my: 0.25, px: "8px" }}
                >
                  <ListItemIcon sx={{ minWidth: 'auto !important', mr: 1 }}><PsychologyIcon fontSize="18px" /></ListItemIcon>
                  <ListItemText
                    primary="Clinical Analysis"
                    secondary="Patient-level clinical data"
                    primaryTypographyProps={{ fontSize: '11px', fontWeight: 600 }}
                    secondaryTypographyProps={{ fontSize: '10px' }}
                  />
                </MenuItem>
              </Menu>
            </Box>
              </Box>
            </Fade>
              </TabPanel>
              <TabPanel value="history" sx={{ padding: 0, height: '100%', minHeight: 0 }}>
            <Fade in timeout={250} key="history-fade">
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <Box sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              p: 1.5,
              minHeight: 0, // Important for flex child to shrink
              overflow: 'hidden', // Prevent content from overflowing
              height: '100%',
              backgroundColor: (theme) => theme.palette.grey[50],
            }}>
              {/* Search Bar + Clear all */}
              <Box sx={{ pr: '8px', display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                size="small"
                type="search"
                placeholder="Search chats..."
                variant="outlined"
                value={historySearch}
                onChange={(e) => setHistorySearch(e.target.value)}
                sx={{ width: '100%' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  sx: {
                    fontSize: '12px',
                    fontWeight: 400,
                    height: '32px',
                    '& input::placeholder': {
                      color: 'text.secondary',
                      opacity: 1, // important (default is lower opacity)
                      fontSize: '12px',
                      fontWeight: 400,
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderRadius: '8px',
                      borderColor: '#c4c4c4',
                      borderWidth: '1px',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#c4c4c4',
                      borderWidth: '1px',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#1976d2',
                      borderWidth: '1px',
                    },
                  },
                }}
              />
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon fontSize="small" />}
                onClick={() => setClearAllDialog(true)}
                disabled={historyLoading || historyItems.length === 0 || actionLoading}
                sx={{
                  textTransform: 'none',
                  fontSize: '12px',
                  height: '32px',
                  borderRadius: '8px',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  letterSpacing: '0.2px',
                  '& .MuiButton-startIcon': { mr: 0.4 },
                }}
              >
                Clear all
              </Button>
              </Box>
              <Box sx={{
                overflowY: 'auto',
                scrollbarGutter: 'stable',
                '&::-webkit-scrollbar': {
                width: 8,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: (theme) => theme.palette.grey[100],
                borderRadius: 4,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: (theme) => theme.palette.grey[400],
                borderRadius: 4,
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.grey[600],
                },
              },
              scrollbarWidth: 'thin',
              scrollbarColor: (theme) => `${theme.palette.grey[400]} ${theme.palette.grey[100]}`,
                }} display="flex" flexDirection="column" gap={1.5}>
                {historyLoading && historyItems.length === 0 && (
                  Array.from({ length: 3 }).map((_, index) => (
                    <Box key={`skeleton-${index}`} border="1px solid #e1e5e9" borderRadius="8px" p={1.5} sx={{ opacity: 0.6 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} mb={0.5}>
                        <Box sx={{ height: 16, width: '60%', backgroundColor: '#eceff1', borderRadius: '4px' }} />
                        <Box sx={{ height: 12, width: '20%', backgroundColor: '#eceff1', borderRadius: '4px' }} />
                      </Box>
                      <Box sx={{ height: 14, width: '90%', backgroundColor: '#eceff1', borderRadius: '4px', mb: 1.5 }} />
                      <Box sx={{ height: 12, width: '25%', backgroundColor: '#eceff1', borderRadius: '4px' }} />
                    </Box>
                  ))
                )}
                {!historyLoading && historyError && (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '13px', color: 'error.main', mb: 1 }}>{historyError}</Typography>
                    <Typography
                      sx={{ fontSize: '12px', color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={() => setDebouncedHistorySearch(s => s === historySearch.trim() ? s + ' ' : historySearch.trim())}
                    >
                      Retry
                    </Typography>
                  </Box>
                )}
                {!historyLoading && !historyError && historyItems.length === 0 && (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                      {debouncedHistorySearch ? 'No conversations match your search.' : 'No conversations yet.'}
                    </Typography>
                  </Box>
                )}
                {historyItems.map((item) => {
                  const when = item.lastMessageAt || item.updatedAt || item.createdAt;
                  const dateLabel = when ? moment(when).format('MMM DD, YYYY') : '';
                  const timeLabel = when ? moment(when).format('hh:mm A') : '';
                  const isLoadingThis = historyLoadingId === item.id;
                  const itemFacilityIds = Array.isArray(item.facilityIds) ? item.facilityIds : [];
                  const itemFacilityNames = itemFacilityIds
                    .map(id => facilityNameById.get(String(id)))
                    .filter(Boolean);
                  const chipLabels = itemFacilityNames.length > 0
                    ? itemFacilityNames
                    : itemFacilityIds.map(() => 'Facility');
                  return (
                    <Box
                      key={item.id}
                      border="1px solid #e1e5e9"
                      borderRadius="8px"
                      p={1.5}
                      onClick={() => loadConversation(item.id)}
                      sx={{
                        cursor: isLoadingThis ? 'progress' : 'pointer',
                        opacity: isLoadingThis ? 0.6 : 1,
                        transition: 'all 0.3s ease',
                        '&:hover': { backgroundColor: 'white', boxShadow: (theme) => theme.shadows[1] }
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} mb={0.5}>
                        <Typography sx={{ fontWeight: 600, fontSize: '16px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                          {item.title || 'Untitled Conversation'}
                        </Typography>
                        <IconButton
                          size="small"
                          aria-label="conversation actions"
                          onClick={(e) => handleActionMenuOpen(e, item)}
                          sx={{ ml: 0.5, p: 0.25 }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: '14px',
                          color: 'text.primary',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                        mb={1.5}
                      >
                        {item.preview || '\u00A0'}
                      </Typography>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <AccessTimeIcon fontSize="14px" sx={{ color: 'text.secondary' }} />
                          <Typography sx={{ fontSize: '12px', color: 'text.secondary', fontWeight: 500 }}>
                            {[dateLabel, timeLabel].filter(Boolean).join(' · ')}
                          </Typography>
                        </Box>
                        {chipLabels.length > 0 && (
                          <ChipGroup labels={chipLabels} chipMaxWidth={100} />
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
              </Box>
              </Box>
            </Fade>
              </TabPanel>
            </TabContext>
          </Box>
          <Menu
            anchorEl={actionMenu.anchorEl}
            open={Boolean(actionMenu.anchorEl)}
            onClose={handleActionMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            onClick={(e) => e.stopPropagation()}
          >
            <MenuItem onClick={handleRenameClick}>
              <ListItemIcon sx={{ minWidth: 'auto !important', mr: 1 }}><EditIcon fontSize="16px" /></ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: '12px' }}>Rename</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
              <ListItemIcon sx={{ minWidth: 'auto !important', mr: 1 }}><DeleteIcon fontSize="16px" sx={{ color: 'error.main' }} /></ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: '12px', color: 'error.main' }}>Delete</ListItemText>
            </MenuItem>
          </Menu>
          <Dialog
            open={renameDialog.open}
            onClose={() => !actionLoading && setRenameDialog({ open: false, id: null, title: '' })}
            fullWidth
            maxWidth="xs"
          >
            <DialogTitle>Rename conversation</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                fullWidth
                size="small"
                value={renameDialog.title}
                onChange={(e) => setRenameDialog(prev => ({ ...prev, title: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleRenameSubmit();
                  }
                }}
                inputProps={{ maxLength: 200 }}
                sx={{ mt: 1 }}
                InputProps={{
                  sx: {
                    fontSize: '12px',
                    fontWeight: 400,
                    height: '32px',
                    '& input::placeholder': {
                      color: 'text.secondary',
                      opacity: 1, // important (default is lower opacity)
                      fontSize: '12px',
                      fontWeight: 400,
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderRadius: '8px',
                      borderColor: '#c4c4c4',
                      borderWidth: '1px',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#c4c4c4',
                      borderWidth: '1px',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#1976d2',
                      borderWidth: '1px',
                    },
                  },
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setRenameDialog({ open: false, id: null, title: '' })}
                disabled={actionLoading}
                variant='outlined'
                color='error'
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleRenameSubmit}
                disabled={
                  actionLoading ||
                  !renameDialog.title.trim() ||
                  renameDialog.title.trim() === (historyItems.find(i => i.id === renameDialog.id)?.title || '').trim()
                }
              >
                {actionLoading ? <CircularProgress size={18} /> : 'Save'}
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={clearAllDialog}
            onClose={() => !actionLoading && setClearAllDialog(false)}
            fullWidth
            maxWidth="xs"
          >
            <DialogTitle>Clear all conversations?</DialogTitle>
            <DialogContent>
              <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
                This will remove every chat in this history list. This action cannot be undone.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setClearAllDialog(false)} disabled={actionLoading}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleClearAllConfirm}
                disabled={actionLoading}
              >
                {actionLoading ? <CircularProgress size={18} /> : 'Clear all'}
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={deleteDialog.open}
            onClose={() => !actionLoading && setDeleteDialog({ open: false, id: null })}
            fullWidth
            maxWidth="xs"
          >
            <DialogTitle>Delete conversation?</DialogTitle>
            <DialogContent>
              <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
                This action cannot be undone.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setDeleteDialog({ open: false, id: null })}
                disabled={actionLoading}
                variant='outlined'
                sx={{
                  color: 'text.secondary',
                  borderColor: 'grey.400',
                  '&:hover': { borderColor: 'grey.500', backgroundColor: 'grey.100' },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDeleteConfirm}
                disabled={actionLoading}
              >
                {actionLoading ? <CircularProgress size={18} /> : 'Delete'}
              </Button>
            </DialogActions>
          </Dialog>
        </StyledDrawer>
      )}
    </>
  );
};

const propsAreEqual = (prev, next) => {
  if (prev.open !== next.open) return false;
  if (prev.disabled !== next.disabled) return false;
  if (prev.pageType !== next.pageType) return false;
  if (prev.conversationId !== next.conversationId) return false;
  if (prev.vectorStoreId !== next.vectorStoreId) return false;
  if (prev.initialMessage !== next.initialMessage) return false;
  const prevStart = prev.filter?.startDate?.toString() || '';
  const nextStart = next.filter?.startDate?.toString() || '';
  const prevEnd = prev.filter?.endDate?.toString() || '';
  const nextEnd = next.filter?.endDate?.toString() || '';
  if (prevStart !== nextStart || prevEnd !== nextEnd) return false;
  // Ignore deep pageData changes; internal ref reads latest when needed
  return true;
};

export default React.memo(AiChatDrawer, propsAreEqual);
